import {
  type Airfield,
  CIVIL_STANDS,
  MAX_ENTRY_INDEX,
  MIN_EXIT_INDEX,
  RUNWAY_LENGTH,
  type RunwayInfo,
  runwayAccess,
  runwayTakes,
  type StandInfo,
  standFit,
  standTakes,
  TOUCHDOWN_INDEX,
} from './airfield';
import {
  AIRCRAFT_CLASSES,
  AIRCRAFT_PARKING_PER_HOUR,
  AIRLINES,
  AircraftClass,
  type AircraftClassDef,
  type AircraftClassId,
  type AirlineDef,
  airlineById,
  BAG_COMPENSATION,
  BAGS_LOST_CONTRACTOR_PERMILLE,
  BAGS_LOST_PERMILLE,
  BIRD_STRIKE_REPAIR_MINUTES,
  BOARDING_GRACE_MINUTES,
  BRIDGE_PAX_PER_MINUTE,
  CANCELLATION_PENALTY,
  CARGO_FEE_PER_TONNE,
  CARGO_MILLIMINUTES_PER_TONNE,
  CARGO_OFFER_PERCENT,
  CARGO_TONNES,
  CATERING_PER_PAX,
  CATERING_TIME_PERCENT,
  CHARTER_DAYS,
  CHARTER_EVENTS,
  CHARTER_FEE_PERCENT,
  CHARTER_PERCENT,
  DEFECT_PERMILLE,
  EMERGENCY_CLOSURE_MINUTES,
  EMERGENCY_PERMILLE,
  FAST_STAND_PAX_PER_MINUTE,
  FAST_STAND_TIME_PERCENT,
  HOTEL_COMFORT,
  HOTEL_CREW_ROOMS,
  HOTEL_GUESTS_PERMILLE,
  HOTEL_ROOM_RATE,
  HOTEL_ROOMS,
  INTERNATIONAL_FEE_PERCENT,
  INTERNATIONAL_RETAIL_PERCENT,
  INTERNATIONAL_TERMINAL_LEVEL,
  isInternational,
  MAINTENANCE_FEE,
  MAX_FLIGHTS_PER_DAY,
  MAX_HOLD_MINUTES,
  MAX_OPEN_OFFERS,
  MEDICAL_FALLBACK_MINUTES,
  MIN_CONNECTION_MINUTES,
  MISSED_CONNECTION_COST,
  NIGHT_STOP_DEPART_FROM,
  NIGHT_STOP_DEPART_SPREAD,
  NIGHT_STOP_FROM,
  NIGHT_STOP_PERCENT,
  NIGHT_STOP_UNTIL,
  NOISE_BUZZ_PER_POINT,
  NOISE_FINE_PER_POINT,
  NOISE_POINTS,
  noiseQuota,
  OFFER_DAYS,
  OFFER_LIFETIME_HOURS,
  ON_TIME_MINUTES,
  PARK_MIN_MINUTES,
  PARKING_MAX_LEVELS,
  PARKING_PER_PAX_PER_LEVEL,
  PASSENGER_FEE,
  RAIL_COMFORT,
  RAIL_PER_PAX,
  RAIL_SATISFACTION,
  REMOTE_PAX_PER_MINUTE,
  RENEWAL_BONUS_SCORE,
  RENEWAL_MIN_SCORE,
  RENTAL_DAYS_MAX,
  RENTAL_DAYS_MIN,
  RENTAL_OFFER_PERCENT,
  REPAIR_MINUTES,
  RETAIL_PER_PAX,
  SCHEDULE_TAXI_MINUTES,
  SHOW_UP_END_MINUTES,
  SHOW_UP_START_MINUTES,
  StandKind,
  type StandKindId,
  SUSPEND_SCORE,
  SUSPEND_STRIKES,
  TERMINAL_PAX_PER_MINUTE,
  TERMINATION_PERCENT,
  TOLL_PER_PAX,
  TOW_BACK_MINUTES,
  TRANSFER_PERCENT_MAX,
  TRANSFER_PERCENT_PER_ROUTE,
  TRANSFER_WINDOW_MINUTES,
  TRANSIT_PER_PAX,
  TRANSIT_REACH,
  TRANSIT_SATISFACTION,
  UPKEEP_PER_DAY_PERMILLE,
  WILDLIFE_AVERTED_PERCENT,
} from './airlines';
import {
  AIRSHOW_ANNOUNCE_MINUTE,
  AIRSHOW_BUZZ,
  AIRSHOW_DECK_INCOME,
  AIRSHOW_MINUTE,
  AIRSHOW_TOWER_LEVEL,
  airshowDay,
} from './airshow';
import {
  BUILDINGS,
  type Building,
  BuildingType,
  dollars,
  footprint,
  isRunway,
  RunwayMode,
  RunwayUsage,
  runwayGrade,
  runwayLights,
  Size,
  towerLevel,
} from './buildings';
import { DAYS_PER_SEASON, loadShift, Season, season, seasonOffers } from './calendar';
import { departureMilestone } from './celebrations';
import { dayIndex, isNight, minuteOfDay, NIGHT_START } from './clock';
import { CITY_COMFORT, cityComfort, cityHour } from './commerce';
import { TICKS_PER_GAME_DAY, TICKS_PER_GAME_HOUR, TICKS_PER_GAME_MINUTE } from './constants';
import {
  type DailyGoal,
  type GoalProgress,
  GoalState,
  goalMet,
  judgedAtDayEnd,
  rollGoals,
} from './daily';
import {
  Facility,
  type FacilityId,
  facilityCount,
  LOUNGE_COMFORT,
  LOUNGE_PER_PAX,
  METRO_COMFORT,
  METRO_PER_PAX,
  METRO_SATISFACTION,
  METRO_SHARE_PERCENT,
  SHOP_PERCENT,
  stagePercents,
} from './facilities';
import {
  airportValue,
  fareGrudge,
  feeGrudge,
  type Loan,
  OVERDRAFT_OFFER_PERCENT,
  offerRatePermille,
  overdraftInterest,
  payInstalments,
} from './finance';
import {
  FLYOVER_BUZZ,
  FLYOVER_COMPENSATION,
  FLYOVER_EARLIEST,
  FLYOVER_LATEST,
  FLYOVER_MINUTES,
  flyoverMinute,
} from './flyover';
import { type GrantEvent, updateGrant } from './grant';
import type { Ground } from './ground';
import {
  acquire,
  arrived,
  CONTRACTOR_TIME_PERCENT,
  DEICE_FEE,
  type Fleet,
  GSE_COUNT,
  Gse,
  groundFleet,
  gseBit,
  HANDLING_FEE,
  ownVehicles,
  PAD_TIME_PERCENT,
  PUSHBACK_TILES,
  REMOTE_DOOR_VEHICLES,
  release,
} from './handling';
import { motorwayCars } from './junctions';
import { type MarketChange, marketFuel, marketLoad, marketOffers, updateMarket } from './market';
import { ROADSHOW_OFFER_PERCENT } from './marketing';
import { MILESTONES, type MilestoneFacts, milestoneDone, withMilestone } from './milestones';
import {
  ClosureKind,
  FOD_CLAIM,
  FOD_REPAIR_MINUTES,
  fodPermille,
  WEAR_MAX,
  WEAR_PER_LANDING,
  WEAR_POOR,
  WEAR_WORN,
} from './pavement';
import {
  buzzOfferPermille,
  CRISIS_BUZZ,
  calmDown,
  hit,
  priceScore,
  RatingCategory,
  rate,
  stir,
  VIRAL_BUZZ,
} from './reputation';
import { Rng } from './rng';
import {
  BLIZZARD_DAYS,
  BLIZZARD_FIRST_DAY,
  BLIZZARD_HOURS_SPAN,
  BLIZZARD_MIN_HOURS,
  BLIZZARD_WARNING,
  PLOUGH_CONTRACTOR_FEE,
  ploughJob,
  type RunwaySnow,
  SNOW_CLEARING,
  SNOW_CLOSE,
  SNOW_CONTRACTOR_FEE,
  SNOW_PER_HOUR,
  SNOW_WAIT,
  SNOW_WARN,
  Snowfall,
  type SnowJob,
  setSnowDepth,
  snowDepth,
} from './snow';
import { SPOTTER_BUZZ, SPOTTER_RANGE, spotterFee } from './spotters';
import {
  atcExtraTicks,
  autoStaff,
  fireCategory,
  GROUND_CONTRACT_BELOW,
  hourlyPayroll,
  STRIKE_HOURS,
  StaffRole,
  staffing,
  strikeChance,
  TOWER_CLOSED_BELOW,
} from './staff';
import { hasBlizzards, typicalClimate } from './theme';
import {
  pickVip,
  rollVip,
  VIP_FIRST_HOUR,
  VIP_LAST_HOUR,
  VIP_NOTICE_MINUTES,
  VIP_PRAISE,
  VIP_SCORN,
  VIP_TOWER_LEVEL,
  vipKindsFor,
  vipPleased,
  vipReward,
} from './vip';
import {
  icing,
  Sky,
  type SkyId,
  temperatureAt,
  updateClimate,
  updateWeatherAhead,
  visibilityMinimum,
} from './weather';
import { crosswind, headwind, runwayHeading, updateRunwayFlows, updateWind } from './wind';

// ---------------------------------------------------------------------------------------------
// State types (saved, hashed)

export const FlightStatus = {
  Scheduled: 1,
  Holding: 2,
  Arriving: 3,
  AtStand: 4,
  Departing: 5,
  Departed: 6,
  Cancelled: 7,
} as const;
export type FlightStatusId = (typeof FlightStatus)[keyof typeof FlightStatus];

/** Why a flight is waiting: shown to the player ("why is this flight delayed?"). */
export const HoldReason = {
  None: 0,
  NoTerminal: 1,
  NoRunway: 2,
  NoStand: 3,
  NoRoute: 4,
  RunwayBusy: 5,
  Night: 6,
  TaxiwayBusy: 7,
  /** Crosswind above what the aircraft may land or take off in on every usable runway. */
  Wind: 8,
  /** Every pushback tug of the ground handling base is busy. */
  NoTug: 9,
  /** Visibility below the runway's minimum (fog): only ILS runways (level 3) operate. */
  LowVisibility: 10,
  /** Being de-iced before take-off. */
  Deicing: 11,
  /** No fire station of the category this aircraft needs (ICAO rescue and fire fighting). */
  NoFireCover: 12,
  /** Too few air traffic controllers on duty to keep the tower open. */
  NoControllers: 13,
  /** The only suitable runway is closed for works (an upgrade under way). */
  RunwayClosed: 14,
  /** No free stand has a road its ground vehicles can reach it by. */
  NoStandAccess: 15,
  /** The runway is closed after an emergency (inspection and clean-up). */
  RunwayEmergency: 16,
  /** The runways are closed while snow is cleared from them. */
  RunwaySnow: 17,
  /** The runway is closed for resurfacing (see pavement.ts). */
  RunwayWorks: 18,
  /** The runways military aircraft use are closed: fighters are scrambling. */
  Scramble: 19,
  /** Went around and found no new slot within the holding time: diverted elsewhere. */
  Diverted: 20,
  /** A helicopter with no free helipad (or none with a road to it). */
  NoHelipad: 21,
  /** At its stand, waiting for ground vehicles (stairs, bus, baggage, fuel…) all busy. */
  NoVehicles: 22,
  /** Every runway is closed while an Air Force flight passes over (see flyover.ts). */
  Flyover: 23,
} as const;
export type HoldReasonId = (typeof HoldReason)[keyof typeof HoldReason];

export const Phase = {
  Approach: 1,
  Landing: 2,
  TaxiIn: 3,
  AtStand: 4,
  Ready: 5,
  TaxiOut: 6,
  Takeoff: 7,
  Climb: 8,
  /** Went around: climbs away, flies the circuit and comes in again for its new slot. */
  GoAround: 9,
  /** On the de-icing pad, on the way from its stand to the runway. */
  Deicing: 10,
  /** Towed between its stand and a remote parking position (standId: where it is going). */
  Tow: 11,
  /** Waiting on a remote parking position (a night stop or a rental). */
  Parked: 12,
} as const;
export type PhaseId = (typeof Phase)[keyof typeof Phase];

/** Kinds of stay on the ground (Flight.park): a turnaround, a night stop, a parking rental. */
export const Stay = { Turnaround: 0, NightStop: 1, Rental: 2 } as const;

export interface Offer {
  readonly id: number;
  readonly airline: number;
  readonly cls: AircraftClassId;
  readonly flightsPerDay: number;
  /** Minutes added to every slot, so contracts don't all land on the hour. */
  readonly slotOffset: number;
  readonly days: number;
  readonly expiresAt: number;
  /** 1 if the airline offers to renew a contract that just ended. */
  readonly renewal: number;
  /** Arrival times as minute of day (empty in saves from before they were planned per offer). */
  readonly slots: readonly number[];
  /** 1 for a freight contract. */
  readonly cargo: number;
  /** Charter for an event (1-based index into CHARTER_EVENTS), 0 for a regular contract. */
  readonly event: number;
  /** A parking rental: days the aircraft stays parked (0: a flight contract). */
  readonly rental: number;
}

export interface Contract {
  readonly id: number;
  readonly airline: number;
  readonly cls: AircraftClassId;
  /** Arrival times as minute of day. */
  readonly slots: readonly number[];
  readonly endsAt: number;
  served: number;
  cancelled: number;
  revenue: number;
  /** Airline satisfaction with this contract, 0–100: punctuality and cancellations. */
  score: number;
  /** Flights in a row the airport could not handle (reset by each departure). */
  strikes: number;
  /** 1 for a freight contract. */
  readonly cargo: number;
  /** Charter event (see Offer), 0 for a regular contract. */
  readonly event: number;
  /** A parking rental: days the aircraft stays parked (0: a flight contract). */
  readonly rental: number;
  /** Why the airline is pleased or not: what its flights here went through (all time). */
  readonly report: ContractReport;
}

/**
 * A contract's record, for the airline's view of the airport (the contract card): what moved
 * its score. Departures on time or late by the airport's doing (inbound delay forgiven), the
 * minutes lost by cause, passengers left behind, bags lost and the fee grudge.
 */
export interface ContractReport {
  onTime: number;
  late: number;
  /** Minutes of delay the airport caused, in all. */
  delayMinutes: number;
  /** Minutes lost by cause (Flight.lostVehicles … lostTechnical summed). */
  lostVehicles: number;
  lostPassengers: number;
  lostDeparture: number;
  lostArrival: number;
  lostTechnical: number;
  booked: number;
  missed: number;
  bagsLost: number;
  /** Score points the fees took off, summed over departures (see feeGrudge). */
  feePoints: number;
}

/** A new contract's record: nothing flown yet. */
export function emptyReport(): ContractReport {
  return {
    onTime: 0,
    late: 0,
    delayMinutes: 0,
    lostVehicles: 0,
    lostPassengers: 0,
    lostDeparture: 0,
    lostArrival: 0,
    lostTechnical: 0,
    booked: 0,
    missed: 0,
    bagsLost: 0,
    feePoints: 0,
  };
}

/** The record's fields in order (snapshot and hash). */
export const REPORT_FIELDS = [
  'onTime',
  'late',
  'delayMinutes',
  'lostVehicles',
  'lostPassengers',
  'lostDeparture',
  'lostArrival',
  'lostTechnical',
  'booked',
  'missed',
  'bagsLost',
  'feePoints',
] as const satisfies readonly (keyof ContractReport)[];

export interface Flight {
  readonly id: number;
  readonly contractId: number;
  readonly airline: number;
  readonly cls: AircraftClassId;
  /** Planned arrival (game ticks). */
  readonly scheduledAt: number;
  status: FlightStatusId;
  holdReason: HoldReasonId;
  aircraftId: number;
  standId: number;
  revenue: number;
  /** When the flight departed or was cancelled; 0 while active. */
  finishedAt: number;
  /** Scheduled departure (game ticks): arrival slot + turnaround + taxi buffer. */
  readonly departAt: number;
  /** Arriving passengers on board when it lands. */
  readonly paxIn: number;
  /** Booked departing passengers. */
  readonly paxOut: number;
  /** Departing passengers through check-in and security (ready at the gate). */
  paxReady: number;
  /** Departing passengers who have reached the terminal by road (see roadArrivals). */
  paxArrived: number;
  /** Departing passengers through check-in and bag drop. */
  paxCheckedIn: number;
  /** Departing passengers through security (international ones still face passport control). */
  paxScreened: number;
  paxBoarded: number;
  /** Passenger-minutes spent queueing at check-in/security. */
  waitSum: number;
  /** Push-back time (game ticks), 0 until then. */
  offBlockAt: number;
  /** 1 once boarding has closed (late passengers miss the flight). */
  gateClosed: number;
  /** 0–100, set at departure. */
  satisfaction: number;
  /**
   * Delay diagnosis: game minutes lost waiting to land (holding), for ground vehicles, for late
   * passengers after the scheduled departure, and ready at the stand waiting to leave.
   */
  lostArrival: number;
  lostVehicles: number;
  lostPassengers: number;
  lostDeparture: number;
  /** Minutes the departure waited only for a technical repair. */
  lostTechnical: number;
  /** Freight carried (tonnes); 0 for a passenger flight. */
  cargo: number;
  /**
   * Minutes late from its origin (weather, congestion, knock-on delays elsewhere): it arrives
   * this much after its slot. Passengers and airlines do not hold it against the airport.
   */
  lateBy: number;
  /** Arriving passengers connecting to another flight here. */
  transferIn: number;
  /** Connecting passengers who took empty seats on this departure. */
  transferOut: number;
  /** Charter event of its contract (kept on the flight: the contract may end first). */
  event: number;
  /** Minutes the turnaround lost to the weather: lightning alerts, half of a sandstorm. */
  lostWeather: number;
  /** How long it stays on the ground (Stay): a turnaround, a night stop or a rental. */
  park: number;
  /** A VIP aboard (VipKind, see vip.ts), 0 for none. */
  vip: number;
}

/** Connecting passengers waiting airside: how many, and when their flight arrived (ticks). */
export interface TransferGroup {
  at: number;
  n: number;
}

export interface Aircraft {
  readonly id: number;
  readonly flightId: number;
  readonly cls: AircraftClassId;
  readonly airline: number;
  phase: PhaseId;
  runwayId: number;
  /** Direction of use on that runway (runway flow at planning time). */
  runwayDir: number;
  /** Runway exit (arrivals) or entry (departures) index, counted along runwayDir. */
  runwayIndex: number;
  /** Position along the runway in runwayDir, 1/1000 tile. */
  runwayPos: number;
  /** 1/1000 tile per tick. */
  speed: number;
  /** Taxi route (tile indices). */
  path: number[];
  /** Position along the path, 1/1000 tile. */
  pathPos: number;
  standId: number;
  timer: number;
  timerTotal: number;
  /** Ticks left per ground service (see Service); filled when the aircraft reaches the stand. */
  svcLeft: number[];
  svcTotal: number[];
  /** 0 waiting, 1 boarding, 2 boarding closed. */
  boardState: number;
  /** Boarding progress in milli-passengers. */
  boardMilli: number;
  /** 1 if fuel comes from the airport's own depot (faster, and sold at a margin). */
  fuelFromDepot: number;
  /** Ground vehicles held right now, one bit per Gse kind. */
  gse: number;
  /** 1 if the outside contractor handles this turnaround (no own ground handling base). */
  contracted: number;
  /** De-icing before take-off: ticks left and planned (0 when not needed). */
  deiceLeft: number;
  deiceTotal: number;
  /** Repair of a technical defect found during the turnaround: ticks left and planned. */
  techLeft: number;
  techTotal: number;
  /** Ticks until each held vehicle (per Gse kind) arrives at the aircraft after driving over. */
  gseEta: number[];
  /** Ticks until the outside contractor's crew arrives (contracted turnarounds). */
  crewEta: number;
  /** Emergency declared by this flight (EmergencyKind), 0 for none. */
  emergency: number;
}

/** Emergencies a flight can declare. Saved: append only. */
export const EmergencyKind = { None: 0, BirdStrike: 1, EngineFailure: 2, Medical: 3 } as const;
export type EmergencyKindId = (typeof EmergencyKind)[keyof typeof EmergencyKind];

/** A runway closed after an emergency until the given tick. */
export interface RunwayClosure {
  readonly runwayId: number;
  /** Closed from this tick (works may be planned ahead) until that one. */
  readonly from: number;
  readonly until: number;
  /** Why (ClosureKind). */
  readonly kind: number;
}

/** Ground services during a turnaround, in the order they are stored. */
export const Service = {
  Deboard: 0,
  Unload: 1,
  Fuel: 2,
  Clean: 3,
  Catering: 4,
  Load: 5,
} as const;
export const SERVICE_COUNT = 6;

/** Money and service figures of one game day. */
export interface Ledger {
  day: number;
  landing: number;
  passenger: number;
  retail: number;
  fuel: number;
  parking: number;
  upkeep: number;
  penalties: number;
  departures: number;
  passengers: number;
  missed: number;
  /** Departures at most ON_TIME_MINUTES late. */
  onTime: number;
  /** Sum of departure delays, minutes. */
  delayMinutes: number;
  cancelled: number;
  /** Fees paid to the outside ground handling contractor (cents). */
  handling: number;
  /** Minutes lost by cause over the day's departures (see Flight). */
  lostArrival: number;
  lostVehicles: number;
  lostPassengers: number;
  lostDeparture: number;
  lostTechnical: number;
  /** Minutes flights arrived late from their origin (not the airport's doing). */
  lostInbound: number;
  /** Fines for night movements over the noise quota (cents). */
  noiseFines: number;
  /** Hotel room revenue (cents), and guests who stayed. */
  hotel: number;
  hotelGuests: number;
  /** Connecting passengers flown out, and those who missed their connection. */
  transfers: number;
  missedConnections: number;
  /** Interest on a negative balance (cents). */
  overdraft: number;
  /** Meals the flight kitchen sold to the airlines (cents). */
  catering: number;
  /** Marketing campaigns paid for (cents). */
  marketing: number;
  /** Minutes turnarounds stood still for lightning. */
  lostWeather: number;
  /** Bags that missed their flight (each compensated under penalties). */
  bagsLost: number;
  /** Paid by the ministry: military sorties and scrambles (cents). */
  military: number;
  /** Line maintenance fees earned by the airport's hangar (cents). */
  maintenance: number;
  /** Staff salaries (cents). */
  wages: number;
  /** Loan instalments paid, interest included (cents). */
  loans: number;
  /** Taxi and bus concession from the transit hub (cents). */
  transit: number;
  /** Rewards for milestones reached (cents). */
  rewards: number;
  /** Freight handling charges (cents) and tonnes handled. */
  cargo: number;
  tonnes: number;
  /** Parking charges for aircraft on remote positions (cents). */
  aircraftParking: number;
  /** Tolls paid at the plazas on the way in (cents). */
  tolls: number;
  /** The airport city (commerce.ts): the airport's take of sales, its customers, office rent. */
  commerce: number;
  customers: number;
  rent: number;
}

export function emptyLedger(day: number): Ledger {
  return {
    day,
    landing: 0,
    passenger: 0,
    retail: 0,
    fuel: 0,
    parking: 0,
    upkeep: 0,
    penalties: 0,
    departures: 0,
    passengers: 0,
    missed: 0,
    onTime: 0,
    delayMinutes: 0,
    cancelled: 0,
    handling: 0,
    lostArrival: 0,
    lostVehicles: 0,
    lostPassengers: 0,
    lostDeparture: 0,
    lostTechnical: 0,
    lostInbound: 0,
    noiseFines: 0,
    hotel: 0,
    hotelGuests: 0,
    transfers: 0,
    missedConnections: 0,
    overdraft: 0,
    catering: 0,
    marketing: 0,
    lostWeather: 0,
    bagsLost: 0,
    military: 0,
    aircraftParking: 0,
    tolls: 0,
    maintenance: 0,
    wages: 0,
    loans: 0,
    transit: 0,
    rewards: 0,
    cargo: 0,
    tonnes: 0,
    commerce: 0,
    customers: 0,
    rent: 0,
  };
}

/** The figures a ledger adds up (all but its day). */
const LEDGER_SUMS = (Object.keys(emptyLedger(0)) as (keyof Ledger)[]).filter((k) => k !== 'day');

/**
 * Adds a finished day into the all-time ledger (World.allTime), whose `day` counts the days in
 * it. The report book reads it with today's figures added (see allTimeLedger).
 */
export function addLedger(into: Ledger, l: Ledger): void {
  for (const k of LEDGER_SUMS) into[k] += l[k];
  into.day++;
}

/** All time so far: the finished days and today. */
export function allTimeLedger(w: { allTime: Ledger; today: Ledger }): Ledger {
  const l = { ...w.allTime };
  addLedger(l, w.today);
  return l;
}

export function ledgerIncome(l: Ledger): number {
  return (
    l.landing +
    l.passenger +
    l.retail +
    l.fuel +
    l.parking +
    l.maintenance +
    l.transit +
    l.rewards +
    l.cargo +
    l.hotel +
    l.catering +
    l.military +
    l.aircraftParking +
    l.tolls +
    l.commerce +
    l.rent
  );
}

/** Everything the airport paid out that day (construction aside). */
export function ledgerCosts(l: Ledger): number {
  return (
    l.upkeep +
    l.penalties +
    l.handling +
    l.wages +
    l.loans +
    l.noiseFines +
    l.overdraft +
    l.marketing
  );
}

/** What the operations systems need from the world. Implemented by World. */
export interface OpsWorld {
  time: number;
  money: number;
  readonly rng: Rng;
  readonly ground: Ground;
  readonly buildings: Building[];
  readonly offers: Offer[];
  readonly contracts: Contract[];
  readonly flights: Flight[];
  readonly aircraft: Aircraft[];
  nextOpsId: number;
  today: Ledger;
  yesterday: Ledger;
  /** Every finished day added up (see addLedger); `day` counts them. */
  allTime: Ledger;
  /** Airport rating 0–1000: the weighted mean of the category ratings (see reputation.ts). */
  rating: number;
  /** Rating per category (RatingCategory), 0–1000. */
  ratings: number[];
  /** Online buzz, -1000 (outrage) to 1000 (praise). */
  buzz: number;
  /** Runways closed after an emergency or for works (some planned ahead). */
  readonly runwayClosures: RunwayClosure[];
  /** Milestones reached (bits, see milestones.ts) and departures served by aircraft class. */
  milestones: number;
  readonly departuresByClass: number[];
  /** Compass step (0–15) the wind blows from, and its speed in knots. */
  windDir: number;
  windSpeed: number;
  /** Sky condition, visibility (100 m units) and mean temperature of the days (see weather.ts). */
  /** Oil price (percent of normal) and the economy (-100 … 100), see market.ts. */
  oilIndex: number;
  economy: number;
  /** What the market is in (MarketFlag bits). */
  marketFlags: number;
  sky: SkyId;
  /** Forecast sky for the coming hours (see updateWeatherAhead). */
  readonly skyAhead: number[];
  visibility: number;
  climate: number;
  /** State incentive grant (see grant.ts). */
  grantState: number;
  grantAmount: number;
  grantDeadline: number;
  /** Staff hired per role and the roles on automatic staffing (see staff.ts). */
  staffHired: number[];
  staffAuto: number;
  staffNight: number[];
  wagePct: number;
  strikeUntil: number;
  /** Steps of the sound insulation programme paid (see noiseQuota). */
  readonly noiseInsulation: number;
  /** Widebodies may still use level 2 medium stands (an older save; see standTakes). */
  legacyWideStands: boolean;
  /** The map (MapTheme, theme.ts): its weather and temperatures. */
  readonly theme: number;
  /** Noise quota points used since the night began (see NOISE_QUOTA). */
  nightNoise: number;
  /** Connecting passengers waiting airside, oldest first. */
  readonly transferPool: TransferGroup[];
  /** Snow lying on each runway, and plough teams out (see snow.ts). */
  readonly runwaySnow: RunwaySnow[];
  readonly snowJobs: SnowJob[];
  /** Today's goals (see daily.ts). */
  readonly dailyGoals: DailyGoal[];
  /** VIPs who left pleased, and air shows flown, ever (for achievements). */
  vipsPleased: number;
  airshows: number;
  /** The airlines that have flown from here, by id, in order (the airline album). */
  readonly airlinesSeen: number[];
  /** The world's seed (daily goals draw from it, see newGoals). */
  readonly seed: number;
  /** How hard it snows now (Snowfall); the winter's blizzard: planned for, and lasting until. */
  snowfall: number;
  blizzardAt: number;
  blizzardUntil: number;
  /** A marketing roadshow brings more offers until this tick. */
  promoUntil: number;
  /** Bank loans being repaid, and fee settings in percent of the tariff (see finance.ts). */
  readonly loans: Loan[];
  landingFeePct: number;
  paxFeePct: number;
  /** Render bookkeeping: set when a building changes (e.g. a runway turns with the wind). */
  buildingsDirty: boolean;
  /** How the roads in coped last minute (worked out every minute; for display only). */
  landside: LandsideFlow;
  airfield(): Airfield;
  emit(event: OpsEvent): void;
}

export type OpsEvent =
  | { readonly type: 'offerReceived'; readonly offerId: number; readonly airline: number }
  | { readonly type: 'flightArrived'; readonly flightId: number; readonly airline: number }
  | {
      readonly type: 'flightDeparted';
      readonly flightId: number;
      readonly airline: number;
      readonly revenue: number;
      readonly satisfaction: number;
      readonly missed: number;
      /** Minutes after the scheduled departure. */
      readonly delay: number;
      /** A freight flight (no passengers to talk about it). */
      readonly cargo: boolean;
      /** Passengers' bags that did not make it. */
      readonly bagsLost: number;
    }
  | {
      readonly type: 'flightCancelled';
      readonly flightId: number;
      readonly airline: number;
      readonly reason: HoldReasonId;
    }
  | GrantEvent
  | { readonly type: 'milestone'; readonly id: number; readonly reward: number }
  | {
      /** An airline's first departure from here: into the album (`collected` now). */
      readonly type: 'airlineFirst';
      readonly airline: number;
      readonly collected: number;
    }
  | {
      /** A round number of departures reached (celebrations.ts). */
      readonly type: 'celebration';
      readonly departures: number;
      readonly reward: number;
    }
  | {
      /** An air show (airshow.ts): announced in the morning, flown in the afternoon. */
      readonly type: 'airshow';
      readonly stage: 'announced' | 'flown';
      /** The runway it is flown along, and what the spotters' decks took. */
      readonly runwayId: number;
      readonly income: number;
    }
  | {
      /** A VIP (vip.ts) booked on a flight, and how they left: pleased (and paying) or not. */
      readonly type: 'vip';
      readonly stage: 'announced' | 'pleased' | 'disappointed';
      readonly flightId: number;
      readonly airline: number;
      readonly kind: number;
      readonly reward: number;
    }
  | {
      /** Daily goals (daily.ts): a new day's set, one met (and paid) or one missed. */
      readonly type: 'dailyGoal';
      readonly stage: 'new' | 'met' | 'missed';
      readonly kind: number;
      readonly reward: number;
    }
  /** A night movement over the noise quota: fined, and residents complain. */
  | {
      readonly type: 'noiseComplaint';
      readonly flightId: number;
      readonly points: number;
      readonly fine: number;
    }
  | {
      /** An emergency: declared on approach, the aircraft down safely, the runway open again. */
      /** Underpaid staff walked out, or came back. */
      readonly type: 'strike';
      readonly stage: 'started' | 'ended';
    }
  | {
      /** The oil price or the economy turned (see market.ts). */
      readonly type: 'market';
      readonly change: MarketChange;
    }
  | {
      /** An arrival went around (and came in again later, or was diverted). */
      readonly type: 'goAround';
      readonly flightId: number;
      readonly airline: number;
      readonly runwayId: number;
      readonly diverted: boolean;
    }
  | {
      /**
       * An Air Force flypast (flyover.ts): every runway closes for it, and what the ministry
       * pays for that; then the runways open again.
       */
      readonly type: 'flyover';
      readonly stage: 'notice' | 'over';
      readonly runwayIds: readonly number[];
      /** How long the runways are closed (game minutes), and the ministry's compensation. */
      readonly minutes: number;
      readonly compensation: number;
    }
  | {
      /** Fighters scramble: the runways military aircraft use close to everyone else. */
      readonly type: 'scramble';
      readonly runwayIds: readonly number[];
      readonly bonus: number;
    }
  | {
      /** Resurfacing of a runway began, or it is done (the runway open again, as new). */
      readonly type: 'runwayWorks';
      readonly stage: 'started' | 'done';
      readonly runwayId: number;
    }
  | {
      /** A runway became worn or poor; or debris on it cut a tyre on landing (FOD). */
      readonly type: 'runwayWear';
      readonly stage: 'worn' | 'poor' | 'fod';
      readonly runwayId: number;
      readonly flightId: number;
    }
  | {
      /**
       * Snow on a runway: deep (the crews are falling behind), closed, ploughed by the crews
       * themselves after nobody sent them (overdue), or cleared and open again.
       */
      readonly type: 'snow';
      readonly stage: 'deep' | 'closed' | 'overdue' | 'cleared';
      readonly runwayId: number;
    }
  | {
      /** The winter's blizzard: forecast (BLIZZARD_WARNING ahead), begun or over. */
      readonly type: 'blizzard';
      readonly stage: 'warning' | 'started' | 'ended';
    }
  | {
      readonly type: 'emergency';
      readonly stage: 'declared' | 'landed' | 'cleared';
      readonly kind: EmergencyKindId;
      readonly flightId: number;
      readonly airline: number;
      readonly runwayId: number;
    }
  | {
      /** The online mood crossed into (or out of) a crisis or a wave of praise. */
      readonly type: 'buzzChanged';
      readonly mood: 'crisis' | 'viral' | 'calm';
    }
  | {
      /** The airline walked away: too many cancelled flights, or too unhappy. */
      readonly type: 'contractSuspended';
      readonly contractId: number;
      readonly airline: number;
      readonly reason: 'cancellations' | 'score';
    }
  | {
      readonly type: 'contractEnded';
      readonly contractId: number;
      readonly airline: number;
      /** The airline's renewal offer, or 0 if it does not want to continue. */
      readonly renewalOfferId: number;
    };

// ---------------------------------------------------------------------------------------------
// Tunables (1/1000 tile per tick)

export const APPROACH_TICKS = 80;
export const CLIMB_TICKS = 80;
const LANDING_SPEED = 700;
const LANDING_DECEL = 16;
const ROLLOUT_SPEED = 140;
/** Speed kept on the runway after braking while the exit is still far ahead (fast taxi). */
const RUNWAY_TAXI_SPEED = 400;
/** Near the exit, the speed aimed for falls by 1 per this many milli-tiles still to go. */
const EXIT_SLOWDOWN = 20;
export const TAXI_SPEED = 90;
const TAKEOFF_START_SPEED = 60;
const TAKEOFF_ACCEL = 14;
const LIFTOFF_SPEED = 900;
/** Finished flights stay on the board this long. */
const FINISHED_RETENTION = 6 * TICKS_PER_GAME_HOUR;

export { dayIndex, isNight, isNightMinute, minuteOfDay } from './clock';

// ---------------------------------------------------------------------------------------------
// Contracts

/** Arrival slots (minute of day): as planned in the offer, or spread over 07:00–21:00. */
export function contractSlots(offer: Offer): number[] {
  if (offer.slots.length > 0) return [...offer.slots];
  const slots: number[] = [];
  for (let i = 0; i < offer.flightsPerDay; i++) {
    slots.push(7 * 60 + Math.floor((i * 14 * 60) / offer.flightsPerDay) + offer.slotOffset);
  }
  return slots;
}

/**
 * Hours of the day airlines may fly in: outside the night (22:00–06:00, see clock.ts) with any
 * runway, all day and night with a lit one (level 2+).
 */
const DAY_HOURS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];
const ALL_HOURS = [0, 1, 2, 3, 4, 5, ...DAY_HOURS, 22, 23];

/**
 * Slots for a new offer: each flight in the least busy allowed hour so far (counting existing
 * contracts and open offers), so traffic spreads over the day, and over the night once a lit
 * runway allows night flights.
 */
function planSlots(w: OpsWorld, flights: number, night: boolean, cargo = false): number[] {
  const load = new Array<number>(24).fill(0);
  for (const c of w.contracts) for (const s of c.slots) load[Math.floor(s / 60) % 24]++;
  for (const o of w.offers) for (const s of contractSlots(o)) load[Math.floor(s / 60) % 24]++;
  const hours = night ? ALL_HOURS : DAY_HOURS;
  const slots: number[] = [];
  for (let i = 0; i < flights; i++) {
    let least = Number.MAX_SAFE_INTEGER;
    for (const h of hours) least = Math.min(least, load[h] ?? 0);
    // Night hours count double: airlines prefer the day, but go at night when the day is full.
    const candidates = hours.filter((h) => (load[h] ?? 0) === least);
    const daytime = candidates.filter((h) => DAY_HOURS.includes(h));
    // Freighters fly when the passenger aircraft are on the ground: evening and night.
    const nightly = candidates.filter((h) => h >= 19 || h <= 6);
    const pool = cargo
      ? nightly.length > 0
        ? nightly
        : candidates
      : daytime.length > 0 && w.rng.int(3) > 0
        ? daytime
        : candidates;
    const hour = pool[w.rng.int(pool.length)] ?? 9;
    load[hour] = (load[hour] ?? 0) + 1;
    slots.push(hour * 60 + w.rng.int(60));
  }
  return slots.sort((a, b) => a - b);
}

/** What ending a contract early costs: a share of the revenue of every flight still to come. */
export function terminationFee(c: Contract, time: number): number {
  const def = AIRCRAFT_CLASSES[c.cls];
  const daysLeft = Math.max(0, Math.ceil((c.endsAt - time) / (24 * TICKS_PER_GAME_HOUR)));
  const perFlight = def.landingFee + Math.floor((def.seats * 82) / 100) * PASSENGER_FEE;
  return Math.floor((daysLeft * c.slots.length * perFlight * TERMINATION_PERCENT) / 100);
}

/** Removes a contract and drops its flights that have not landed yet. */
export function endContract(
  w: Pick<OpsWorld, 'contracts' | 'flights' | 'time'>,
  contractId: number,
): void {
  const index = w.contracts.findIndex((c) => c.id === contractId);
  if (index >= 0) w.contracts.splice(index, 1);
  for (const f of w.flights) {
    if (f.contractId !== contractId) continue;
    if (f.status === FlightStatus.Scheduled || f.status === FlightStatus.Holding) {
      f.status = FlightStatus.Cancelled;
      f.finishedAt = w.time;
    }
  }
}

/** The airline gives up on the contract. */
function suspend(w: OpsWorld, c: Contract, reason: 'cancellations' | 'score'): void {
  endContract(w, c.id);
  hit(w, RatingCategory.Reliability, -200);
  talk(w, -250);
  w.emit({ type: 'contractSuspended', contractId: c.id, airline: c.airline, reason });
}

/** Chance (per mille, per hour) of an offer from the ministry while the military zone stands. */
export const MILITARY_OFFER_PERMILLE = 90;
/** Each military tower level raises that chance this much (percent of it). */
export const MILITARY_TOWER_OFFER_PERCENT = 60;

/**
 * How many military offers may be open at once, and contracts each unit may hold: one, and
 * one more for each military tower level.
 */
function militaryRoom(w: OpsWorld): number {
  return 1 + bestLevel(w, BuildingType.MilitaryTower);
}
/** A scramble: chance per hour (per mille) with fighters based, minutes the runways close, pay. */
export const SCRAMBLE_PERMILLE = 45;
export const SCRAMBLE_MINUTES = 12;
export const SCRAMBLE_BONUS = dollars(9_000);
/** Extra the ministry pays (percent) for sorties and scrambles, by air-defence site level. */
export const AIR_DEFENCE_PERCENT: readonly number[] = [0, 15, 30];

/** The ministry's payment raised by the air-defence site, if one is ready. */
function withAirDefence(w: OpsWorld, amount: number): number {
  let level = 0;
  for (const b of w.buildings) {
    if (b.type === BuildingType.AirDefence && b.buildRemaining === 0 && b.level > level)
      level = b.level;
  }
  return Math.floor((amount * (100 + (AIR_DEFENCE_PERCENT[level] ?? 0))) / 100);
}

/** Whether the military zone is ready for a class: the gate and what the class needs. */
function militaryReady(w: OpsWorld, cls: AircraftClassId): boolean {
  // The gate with a road or service road its vehicles reach (see SERVICE_SIDE).
  if (servedLevel(w, BuildingType.MilitaryGate) < 1) return false;
  if (cls === AircraftClass.Fighter) {
    return (
      bestLevel(w, BuildingType.Shelter) >= 1 &&
      bestLevel(w, BuildingType.OpsCenter) >= 1 &&
      bestLevel(w, BuildingType.Munitions) >= 1
    );
  }
  return bestLevel(w, BuildingType.MilitaryApron) >= 1;
}

/**
 * The ministry offers a contract to a unit the military zone can take (one military offer open
 * at a time): a few sorties a day, day and night alike if the runway is lit.
 */
function generateMilitaryOffer(w: OpsWorld): void {
  const room = militaryRoom(w);
  if (w.offers.filter((o) => airlineById(o.airline)?.military === true).length >= room) return;
  // A unit with an offer open, or with as many contracts as the tower lets it hold, is busy.
  const busy = new Set<number>();
  for (const o of w.offers) busy.add(o.airline);
  const held = new Map<number, number>();
  for (const c of w.contracts) held.set(c.airline, (held.get(c.airline) ?? 0) + 1);
  for (const [airline, n] of held) if (n >= room) busy.add(airline);
  const tower = towerLevel(w.buildings);
  const classOf = (a: AirlineDef): AircraftClassId | 0 => {
    for (let i = 0; i < a.fleet.length; i++) {
      if (!a.fleet[i]) continue;
      const cls = (i + 1) as AircraftClassId;
      const def = AIRCRAFT_CLASSES[cls];
      if (def.towerLevel <= tower && runwayFor(w, def) && militaryReady(w, cls)) return cls;
    }
    return 0;
  };
  const free = AIRLINES.filter((a) => a.military === true && !busy.has(a.id) && classOf(a) !== 0);
  const unit = free[w.rng.int(free.length)];
  if (!unit) return;
  const cls = classOf(unit) as AircraftClassId;
  const flightsPerDay = w.rng.range(1, cls === AircraftClass.Fighter ? 3 : 2);
  const offer: Offer = {
    id: w.nextOpsId++,
    airline: unit.id,
    cls,
    flightsPerDay,
    slotOffset: 0,
    days: OFFER_DAYS,
    expiresAt: w.time + OFFER_LIFETIME_HOURS * TICKS_PER_GAME_HOUR,
    renewal: 0,
    slots: planSlots(w, flightsPerDay, litRunway(w), false),
    cargo: 0,
    event: 0,
    rental: 0,
  };
  w.offers.push(offer);
  w.emit({ type: 'offerReceived', offerId: offer.id, airline: offer.airline });
}

/**
 * Now and then, with fighters based here, two of them scramble: every runway military aircraft
 * may use closes to everyone else for a few minutes, and the ministry pays for the readiness.
 */
function scramble(w: OpsWorld): void {
  const fighters = w.contracts.some((c) => c.cls === AircraftClass.Fighter);
  if (!fighters || bestLevel(w, BuildingType.Shelter) < 1) return;
  if (w.runwayClosures.some((c) => c.kind === ClosureKind.Scramble)) return;
  if (w.rng.int(1000) >= SCRAMBLE_PERMILLE) return;
  const runwayIds: number[] = [];
  for (const b of w.buildings) {
    if (!isRunway(b.type) || runwayGrade(b) < 2 || b.buildRemaining > 0) continue;
    if (b.usage === RunwayUsage.Civil) continue;
    if (w.runwayClosures.some((c) => c.runwayId === b.id && c.from <= w.time)) continue;
    w.runwayClosures.push({
      runwayId: b.id,
      from: w.time,
      until: w.time + minutes(SCRAMBLE_MINUTES),
      kind: ClosureKind.Scramble,
    });
    runwayIds.push(b.id);
  }
  if (runwayIds.length === 0) return;
  const bonus = withAirDefence(w, SCRAMBLE_BONUS);
  w.money += bonus;
  w.today.military += bonus;
  w.emit({ type: 'scramble', runwayIds, bonus });
}

/**
 * Remote parking positions not yet promised to a rental (offered or contracted); only those an
 * aircraft can taxi to from a runway count.
 */
function rentalRoom(w: OpsWorld, def?: AircraftClassDef): number {
  const airfield = w.airfield();
  // Of a size that takes the aircraft (when it is known).
  const positions = airfield.stands.filter(
    (s) =>
      s.kind === StandKind.Remote && (!def || standTakes(s, def)) && airfield.standLinked(s.id),
  ).length;
  const promised =
    w.offers.filter((o) => o.rental > 0).length + w.contracts.filter((c) => c.rental > 0).length;
  return positions - promised;
}

/** A finished runway (open or not) that takes the class. */
function runwayFor(w: OpsWorld, def: AircraftClassDef): boolean {
  return w.airfield().runways.some((rw) => runwayTakes(rw, def));
}

/** A finished stand of the class's kind that takes it. */
function standFor(w: OpsWorld, def: AircraftClassDef): boolean {
  return w.airfield().stands.some((s) => s.kind === def.parking && standTakes(s, def));
}

/** A finished runway with lights (night flights). */
function litRunway(w: OpsWorld): boolean {
  return w.buildings.some((b) => isRunway(b.type) && runwayLights(b) >= 2);
}

/**
 * An airline already flying here offers more (another route, more flights) once every airline
 * that can come has a deal, up to this many contracts each.
 */
export const MAX_DEALS_PER_AIRLINE = 3;

function generateOffer(w: OpsWorld): void {
  // One open offer per airline at a time. Airlines with no deal here come first; once every one
  // that can come has one, those already flying here offer more, up to MAX_DEALS_PER_AIRLINE.
  const offering = new Set<number>();
  for (const o of w.offers) offering.add(o.airline);
  const held = new Map<number, number>();
  for (const c of w.contracts) held.set(c.airline, (held.get(c.airline) ?? 0) + 1);
  // With a cargo terminal some of the interest comes from freight airlines.
  const freight =
    servedLevel(w, BuildingType.CargoTerminal) >= 1 && w.rng.int(100) < CARGO_OFFER_PERCENT;
  // International airlines wait for a terminal with passport control.
  const passports = terminalLevel(w) >= INTERNATIONAL_TERMINAL_LEVEL;
  // Airlines start small: they only offer aircraft the airport has a runway and a stand for
  // (regional jets on a first runway, narrowbodies from level 2, widebodies from level 3, the
  // jumbos and giant freighters on a large runway and stand), and the airliners only once the
  // tower has grown past its first level (private jets and small aircraft before that).
  const tower = towerLevel(w.buildings);
  const helipad = bestLevel(w, BuildingType.Helipad) >= 1;
  // The big ones need what they land on and park on built; the rest are offered as before,
  // by the best runway's equipment (a first airfield hears from them before it is built).
  let grade = 1;
  for (const b of w.buildings) if (isRunway(b.type)) grade = Math.max(grade, runwayGrade(b));
  const served = new Map<number, boolean>();
  const canServe = (def: AircraftClassDef) => {
    let ok = served.get(def.id);
    if (ok === undefined) {
      // A big one needs its runway and stand; a widebody its runway level and a large stand.
      ok =
        def.size === Size.Large
          ? runwayFor(w, def) && standFor(w, def)
          : def.runwayLevel <= grade && (def.standSize !== Size.Large || standFor(w, def));
      served.set(def.id, ok);
    }
    return ok;
  };
  const weightsOf = (a: AirlineDef) =>
    a.fleet.map((v, i) => {
      const def = AIRCRAFT_CLASSES[(i + 1) as AircraftClassId];
      if (!def || v === 0) return 0;
      if (def.id === AircraftClass.Helicopter) return helipad && def.towerLevel <= tower ? v : 0;
      return def.towerLevel <= tower && canServe(def) ? v : 0;
    });
  const welcome = AIRLINES.filter(
    (a) =>
      !offering.has(a.id) &&
      a.military !== true &&
      (a.cargo === true) === freight &&
      (passports || !a.international) &&
      weightsOf(a).some((v) => v > 0),
  );
  const fresh = welcome.filter((a) => !held.has(a.id));
  const free =
    fresh.length > 0 ? fresh : welcome.filter((a) => (held.get(a.id) ?? 0) < MAX_DEALS_PER_AIRLINE);
  const airline = free[w.rng.int(free.length)];
  if (!airline) return;
  const weights = weightsOf(airline);
  const total = weights.reduce((a, b) => a + b, 0);
  if (total === 0) return;
  let pick = w.rng.int(total);
  let cls: AircraftClassId = 1;
  for (let i = 0; i < weights.length; i++) {
    pick -= weights[i] ?? 0;
    if (pick < 0) {
      cls = (i + 1) as AircraftClassId;
      break;
    }
  }
  // Now and then an event brings a short charter series of jets.
  const event =
    !freight &&
    (cls === AircraftClass.Narrowbody || cls === AircraftClass.Widebody) &&
    w.rng.int(100) < CHARTER_PERCENT
      ? 1 + w.rng.int(CHARTER_EVENTS.length)
      : 0;
  // A free remote parking position: some airlines would rather park an aircraft here for days.
  const rental =
    !freight &&
    !event &&
    rentalRoom(w, AIRCRAFT_CLASSES[cls]) > 0 &&
    w.rng.int(100) < RENTAL_OFFER_PERCENT
      ? w.rng.range(RENTAL_DAYS_MIN, RENTAL_DAYS_MAX)
      : 0;
  const heli = cls === AircraftClass.Helicopter;
  const flightsPerDay = rental
    ? 1
    : event
      ? w.rng.range(3, MAX_FLIGHTS_PER_DAY)
      : AIRCRAFT_CLASSES[cls].big
        ? 1
        : heli
          ? w.rng.range(2, 4)
          : w.rng.range(1, 3);
  const offer: Offer = {
    id: w.nextOpsId++,
    airline: airline.id,
    cls,
    flightsPerDay,
    slotOffset: 0,
    // A rental runs a day past the departure, so the contract is still there when it leaves.
    days: rental ? rental + 1 : event ? CHARTER_DAYS : OFFER_DAYS,
    expiresAt: w.time + OFFER_LIFETIME_HOURS * TICKS_PER_GAME_HOUR,
    renewal: 0,
    slots: planSlots(w, flightsPerDay, litRunway(w) && !heli, freight),
    cargo: freight ? 1 : 0,
    event,
    rental,
  };
  w.offers.push(offer);
  w.emit({ type: 'offerReceived', offerId: offer.id, airline: offer.airline });
}

// ---------------------------------------------------------------------------------------------
// Reservations in space and time
//
// Taxi tiles and runways are booked only for the window of time an aircraft will really use
// them. Movements are fully predictable and an aircraft never stops on a taxiway or runway, so
// a movement starts only when its whole time-stamped path is free, and once started nothing can
// hold it up. Nobody ever waits while holding a resource, so traffic cannot deadlock, yet many
// aircraft can share taxiways one behind the other and a runway can take the next landing
// while the previous one is still taxiing in. Bookings are derived from the aircraft whenever
// they are needed (never stored), so they cannot leak or desync; as time passes an aircraft's
// bookings only ever shrink.

/**
 * Half the time (ticks) an aircraft blocks a taxi tile around the moment it passes its centre:
 * about half its length plus a margin, at taxi speed. Two aircraft keep both halves apart.
 */
const TAXI_CLEARANCE_TICKS: Readonly<Record<AircraftClassId, number>> = {
  1: 45,
  2: 56,
  3: 89,
  4: 32,
  5: 30,
  6: 62,
  7: 28,
  8: 20,
  9: 20,
  10: 105,
  11: 105,
};
/** The runway is kept clear this long before a touchdown (short final). */
const FINAL_TICKS = 30;
/** Margin around vacating the runway after landing and lining up for take-off. */
const RUNWAY_VACATE_TICKS = 10;
/** Wake-turbulence spacing after a lift-off before the runway is used again. */
const WAKE_TICKS = 30;

/** ICAO wake-turbulence categories. */
export const WakeCategory = { Light: 0, Medium: 1, Heavy: 2 } as const;
export type WakeCategoryId = (typeof WakeCategory)[keyof typeof WakeCategory];

/** Wake category of each aircraft class (by maximum take-off mass). */
export const WAKE_CATEGORY: Readonly<Record<AircraftClassId, WakeCategoryId>> = {
  1: WakeCategory.Medium,
  2: WakeCategory.Medium,
  3: WakeCategory.Heavy,
  4: WakeCategory.Light,
  5: WakeCategory.Medium,
  6: WakeCategory.Medium,
  7: WakeCategory.Light,
  8: WakeCategory.Light,
  9: WakeCategory.Light,
  10: WakeCategory.Heavy,
  11: WakeCategory.Heavy,
};

/**
 * Extra spacing (ticks) a follower keeps behind a leader, on top of the usual: [leader][follower].
 * Behind a heavy a medium waits half a minute more and a light a minute and a half; behind a
 * medium a light waits half a minute more. Nothing extra behind a lighter or equal aircraft.
 */
export const WAKE_EXTRA_TICKS: readonly (readonly number[])[] = [
  [0, 0, 0],
  [10, 0, 0],
  [30, 10, 0],
];

function wakeExtra(leader: number | undefined, follower: number | undefined): number {
  if (leader === undefined || follower === undefined) return 0;
  return WAKE_EXTRA_TICKS[leader]?.[follower] ?? 0;
}

/**
 * Chance (per mille) that an arrival goes around instead of landing, by the weather: a few in a
 * thousand on a calm clear day, many more in a storm, near the visibility minimum or in a strong
 * crosswind. Emergencies always land.
 */
export const GO_AROUND_PERMILLE = 6;
/** The circuit after a go-around: climb out, turn, downwind and back to the start of the final. */
export const GO_AROUND_TICKS = 180;
/** Extra circuit time tried when the runway is busy at the end of a go-around (up to 30 min). */
const GO_AROUND_HOLDING = [0, 20, 40, 60, 90, 120, 180, 240, 300, 400, 500, 600] as const;
/** Stand planning: how much worse (in taxi tiles) a stand of the wrong kind counts. */
const STAND_MISMATCH = 1000;
/** Extra approach ticks tried when the runway is busy: the flight joins the approach later. */
const HOLDING_TICKS = [0, 20, 40, 60, 90, 120] as const;

interface Booking {
  readonly from: number;
  readonly to: number;
  /** Runways: the direction of the movement (none for a closure). */
  readonly dir?: number;
  /** Runways: the wake category of the aircraft (none for a closure). */
  readonly cat?: number;
}

/**
 * Runways used the other way round: an aircraft climbing out must be well clear before one
 * comes in head-on, and the other way round (an approach plus a climb-out).
 */
const OPPOSITE_DIRECTION_TICKS = APPROACH_TICKS + CLIMB_TICKS;

interface Reservations {
  tiles: Map<number, Booking[]>;
  runways: Map<number, Booking[]>;
  stands: Set<number>;
  /**
   * Stands held for aircraft on remote positions due back for their departure (aircraft id →
   * stand): no arrival takes one while the tug is on its way.
   */
  held: Map<number, number>;
  /** Extra runway separation (ticks) of a short-staffed tower. */
  sep: number;
}

function book(map: Map<number, Booking[]>, key: number, b: Booking): true {
  const list = map.get(key);
  if (list) list.push(b);
  else map.set(key, [b]);
  return true;
}

function isFree(map: Map<number, Booking[]>, key: number, b: Booking): boolean {
  const list = map.get(key);
  if (!list) return true;
  for (const o of list) {
    const gap =
      o.dir !== undefined && b.dir !== undefined && o.dir !== b.dir ? OPPOSITE_DIRECTION_TICKS : 0;
    // Clear if it fits after the other (behind its wake) or before it (ahead of it).
    const after = b.from >= o.to + gap + wakeExtra(o.cat, b.cat);
    const before = b.to + gap + wakeExtra(b.cat, o.cat) <= o.from;
    if (!after && !before) return false;
  }
  return true;
}

/**
 * Speed after one more tick of landing roll, `remaining` milli-tiles short of the exit: brake
 * down to a fast taxi while the exit is far, then slow down towards it (never speeding up).
 */
function rollout(v: number, remaining: number): number {
  const target = Math.min(
    RUNWAY_TAXI_SPEED,
    ROLLOUT_SPEED + Math.floor(Math.max(0, remaining) / EXIT_SLOWDOWN),
  );
  return Math.max(ROLLOUT_SPEED, Math.min(v, Math.max(v - LANDING_DECEL, target)));
}

/** Ticks of landing roll until the aircraft turns off at `exit` (mirrors moveAircraft). */
function landingRollTicks(pos: number, speed: number, exit: number): number {
  let p = pos;
  let v = speed;
  let n = 0;
  do {
    n++;
    p += v;
    v = rollout(v, exit * 1000 - p);
  } while (p < exit * 1000);
  return n;
}

/** Ticks of take-off roll until lift-off (mirrors moveAircraft). */
function takeoffRollTicks(pos: number, speed: number): number {
  let p = pos;
  let v = speed;
  let n = 0;
  do {
    n++;
    v += TAKEOFF_ACCEL;
    p += v;
  } while (v < LIFTOFF_SPEED && p < (RUNWAY_LENGTH - 1) * 1000);
  return n;
}

/**
 * Visits the booking of every taxi node on `path` from position `pos` (1/1000 tile) on, for an
 * aircraft taxiing at TAXI_SPEED from tick `start`. Stops early if `visit` returns false.
 */
function taxiBookings(
  path: readonly number[],
  pos: number,
  start: number,
  cls: AircraftClassId,
  airfield: Airfield,
  visit: (tile: number, b: Booking) => boolean,
): boolean {
  const half = TAXI_CLEARANCE_TICKS[cls];
  for (let i = Math.floor(pos / 1000); i < path.length; i++) {
    const tile = path[i] as number;
    if (!airfield.isNode(tile)) continue;
    const at = start + Math.ceil(Math.max(0, i * 1000 - pos) / TAXI_SPEED);
    if (!visit(tile, { from: at - half, to: at + half })) return false;
  }
  return true;
}

/** Runway booking and taxi start of an arrival that touches down `approach` ticks after `now`. */
function arrivalTimes(now: number, approach: number, exit: number, sep: number) {
  const touchdown = now + approach;
  const taxiStart = touchdown + landingRollTicks(TOUCHDOWN_INDEX * 1000, LANDING_SPEED, exit);
  return {
    runway: { from: touchdown - FINAL_TICKS - sep, to: taxiStart + RUNWAY_VACATE_TICKS },
    taxiStart,
  };
}

/** Runway booking of a departure taxiing along `path` from `pos` now, entering at `entry`. */
function departureRunway(
  now: number,
  path: readonly number[],
  pos: number,
  entry: number,
  sep: number,
) {
  const lineUp = now + Math.ceil(((path.length - 1) * 1000 - pos) / TAXI_SPEED);
  return {
    from: lineUp - RUNWAY_VACATE_TICKS,
    to: lineUp + takeoffRollTicks(entry * 1000, TAKEOFF_START_SPEED) + WAKE_TICKS + sep,
  };
}

function reservations(w: OpsWorld): Reservations {
  const sep = atcExtraTicks(staffing(w, StaffRole.Atc));
  const r: Reservations = {
    tiles: new Map(),
    runways: new Map(),
    stands: new Set(),
    held: new Map(),
    sep,
  };
  const airfield = w.airfield();
  const now = w.time;
  const bookTile = (tile: number, b: Booking) => book(r.tiles, tile, b);
  for (const a of w.aircraft) {
    if (a.standId) r.stands.add(a.standId);
    // Helicopters keep to their helipads: no runway, no taxiway.
    if (a.cls === AircraftClass.Helicopter) continue;
    const cat = WAKE_CATEGORY[a.cls];
    switch (a.phase) {
      case Phase.Approach: {
        const t = arrivalTimes(now, a.timer, a.runwayIndex, sep);
        book(r.runways, a.runwayId, { ...t.runway, dir: a.runwayDir, cat });
        taxiBookings(a.path, 0, t.taxiStart, a.cls, airfield, bookTile);
        break;
      }
      case Phase.GoAround: {
        // Its new slot at the end of the circuit, and the climb-out over the runway now.
        const t = arrivalTimes(now, a.timer + APPROACH_TICKS, a.runwayIndex, sep);
        book(r.runways, a.runwayId, { ...t.runway, dir: a.runwayDir, cat });
        taxiBookings(a.path, 0, t.taxiStart, a.cls, airfield, bookTile);
        const left = Math.max(0, WAKE_TICKS - (a.timerTotal - a.timer));
        if (left > 0)
          book(r.runways, a.runwayId, { from: now, to: now + left, dir: a.runwayDir, cat });
        break;
      }
      case Phase.Landing: {
        const taxiStart = now + landingRollTicks(a.runwayPos, a.speed, a.runwayIndex);
        book(r.runways, a.runwayId, {
          from: now,
          to: taxiStart + RUNWAY_VACATE_TICKS,
          dir: a.runwayDir,
          cat,
        });
        taxiBookings(a.path, 0, taxiStart, a.cls, airfield, bookTile);
        break;
      }
      case Phase.TaxiIn:
        taxiBookings(a.path, a.pathPos, now, a.cls, airfield, bookTile);
        break;
      case Phase.TaxiOut:
        taxiBookings(a.path, a.pathPos, now, a.cls, airfield, bookTile);
        book(r.runways, a.runwayId, {
          ...departureRunway(now, a.path, a.pathPos, a.runwayIndex, sep),
          dir: a.runwayDir,
          cat,
        });
        break;
      case Phase.Takeoff:
        book(r.runways, a.runwayId, {
          from: now,
          to: now + takeoffRollTicks(a.runwayPos, a.speed) + WAKE_TICKS + sep,
          dir: a.runwayDir,
          cat,
        });
        break;
      case Phase.Climb: {
        // Kept (if only for a moment) so traffic the other way stays clear of the climb-out.
        const left = Math.max(0, WAKE_TICKS + sep - (CLIMB_TICKS - a.timer));
        book(r.runways, a.runwayId, { from: now, to: now + left, dir: a.runwayDir, cat });
        break;
      }
      default:
        break;
    }
  }
  // Closures, also those planned ahead: no movement may be booked into them.
  for (const c of w.runwayClosures) book(r.runways, c.runwayId, { from: c.from, to: c.until });
  holdStandsForTowBack(w, r);
  return r;
}

/**
 * Aircraft on remote positions due back at a stand for their departure, the longest due
 * first: each gets the first stand that suits it held, so the arrivals that take a stand the
 * moment it is free cannot keep it waiting for days while its tug drives over.
 */
function holdStandsForTowBack(w: OpsWorld, r: Reservations): void {
  const due: { a: Aircraft; f: Flight }[] = [];
  for (const a of w.aircraft) {
    if (a.phase !== Phase.Parked) continue;
    const f = w.flights.find((x) => x.id === a.flightId);
    if (f && f.park !== Stay.Rental && !beforeTowBack(w, f)) due.push({ a, f });
  }
  if (due.length === 0) return;
  due.sort((x, y) => x.f.departAt - y.f.departAt || x.a.id - y.a.id);
  for (const { a } of due) {
    const stand = towTargets(w, a, StandKind.Civil, r)[0];
    if (!stand) continue;
    r.stands.add(stand.id);
    r.held.set(a.id, stand.id);
  }
}

/** Free stands of `kind` an aircraft may be towed to (see tryTow). */
function towTargets(w: OpsWorld, a: Aircraft, kind: StandKindId, r: Reservations): StandInfo[] {
  const airfield = w.airfield();
  const def = AIRCRAFT_CLASSES[a.cls];
  return airfield.stands
    .filter(
      (s) =>
        s.kind === kind &&
        !r.stands.has(s.id) &&
        standTakes(s, def, w.legacyWideStands) &&
        (w.buildings.find((b) => b.id === s.id)?.buildRemaining ?? 1) === 0 &&
        (kind !== StandKind.Civil ||
          airfield.serviceConnected(w.buildings.find((b) => b.id === s.id) as Building)),
    )
    .sort((x, y) => standFit(x.size, def) - standFit(y.size, def) || x.id - y.id);
}

function hasTerminal(w: OpsWorld): boolean {
  return w.buildings.some((b) => b.type === BuildingType.Terminal && b.level >= 1);
}

interface RunwayChoice {
  readonly rw: RunwayInfo;
  readonly flow: number;
}

/**
 * Runways this class may use now, in their current direction, skipping those reserved for the
 * other kind of movement. When none qualifies, `reason` says why.
 */
function usableRunways(
  w: OpsWorld,
  cls: AircraftClassId,
  excludedMode: number,
): { usable: RunwayChoice[]; reason: HoldReasonId } {
  const def = AIRCRAFT_CLASSES[cls];
  if (staffing(w, StaffRole.Atc) < TOWER_CLOSED_BELOW) {
    return { usable: [], reason: HoldReason.NoControllers };
  }
  // No commercial flight without rescue and fire-fighting cover of its category, which needs
  // a crewed fire station.
  const fire = fireCategory(bestLevel(w, BuildingType.FireStation), staffing(w, StaffRole.Fire));
  if (fire < def.fireLevel) {
    return { usable: [], reason: HoldReason.NoFireCover };
  }
  const night = isNight(w.time);
  const usable: RunwayChoice[] = [];
  let reason: HoldReasonId = HoldReason.NoRunway;
  for (const rw of w.airfield().runways) {
    const b = w.buildings.find((x) => x.id === rw.id);
    if (!b || !runwayTakes(rw, def) || b.mode === excludedMode) continue;
    // Civil-only runways take no military aircraft, military-only ones no airliners.
    if (b.usage === (def.military ? RunwayUsage.Civil : RunwayUsage.Military)) continue;
    // A runway being upgraded is closed: no landings or take-offs until the works are done.
    if (b.buildRemaining > 0) {
      if (reason === HoldReason.NoRunway) reason = HoldReason.RunwayClosed;
      continue;
    }
    // …and one being inspected and cleaned after an emergency, or resurfaced.
    const closed = w.runwayClosures.find((c) => c.runwayId === rw.id && c.from <= w.time);
    if (closed) {
      if (reason === HoldReason.NoRunway) {
        reason =
          closed.kind === ClosureKind.Works
            ? HoldReason.RunwayWorks
            : closed.kind === ClosureKind.Scramble
              ? HoldReason.Scramble
              : closed.kind === ClosureKind.Snow
                ? HoldReason.RunwaySnow
                : closed.kind === ClosureKind.Flyover
                  ? HoldReason.Flyover
                  : HoldReason.RunwayEmergency;
      }
      continue;
    }
    if (night && rw.lights < 2) {
      reason = HoldReason.Night;
      continue;
    }
    const heading = runwayHeading(rw.rot, b.flow);
    if (crosswind(w.windDir, w.windSpeed, heading) > def.crosswindLimit) {
      if (reason === HoldReason.NoRunway) reason = HoldReason.Wind;
      continue;
    }
    if (w.visibility < visibilityMinimum(rw.lights)) {
      if (reason === HoldReason.NoRunway) reason = HoldReason.LowVisibility;
      continue;
    }
    usable.push({ rw, flow: b.flow });
  }
  return { usable, reason };
}

// ---------------------------------------------------------------------------------------------
// Arrivals and departures

/** Most tailwind (knots) the tower accepts to use a runway the other way round. */
export const CONTRA_TAILWIND = 10;

/**
 * Directions to try on a runway: the one in use, then, with a light enough tailwind, the other
 * way round, for when no taxiway joins the runway where it is needed in the direction in use
 * (small airfields without a parallel taxiway do this).
 */
function contraFlow(w: OpsWorld, rw: RunwayInfo, flow: number): number[] {
  const back = 1 - flow;
  const tail = -headwind(w.windDir, w.windSpeed, runwayHeading(rw.rot, back));
  return tail <= CONTRA_TAILWIND ? [flow, back] : [flow];
}

/** Tries to start an arrival: needs a free stand and a free slot on a runway and taxi route. */
function tryArrive(w: OpsWorld, f: Flight, r: Reservations): HoldReasonId {
  if (f.cls === AircraftClass.Helicopter) return tryArriveHelicopter(w, f, r);
  const def = AIRCRAFT_CLASSES[f.cls];
  if (!def.military && !hasTerminal(w)) return HoldReason.NoTerminal;
  const airfield = w.airfield();
  const choice = usableRunways(w, f.cls, RunwayMode.Departures);
  if (choice.usable.length === 0) return choice.reason;
  // Stands under works take no new aircraft (one already there finishes its turnaround).
  const open = airfield.stands.filter(
    (s) =>
      s.kind === def.parking &&
      standTakes(s, def, w.legacyWideStands) &&
      !r.stands.has(s.id) &&
      (w.buildings.find((b) => b.id === s.id)?.buildRemaining ?? 0) === 0,
  );
  if (open.length === 0) return HoldReason.NoStand;
  // Ground vehicles come by road or service road: a stand needs a way to them.
  const stands = open.filter((s) => {
    const b = w.buildings.find((x) => x.id === s.id);
    return b !== undefined && airfield.serviceConnected(b);
  });
  if (stands.length === 0) return HoldReason.NoStandAccess;

  const plans: { rw: RunwayInfo; flow: number; stand: StandInfo; exit: number; route: number[] }[] =
    [];
  for (const { rw, flow } of choice.usable) {
    for (const way of contraFlow(w, rw, flow)) {
      for (const stand of stands) {
        const plan = arrivalRoute(airfield, rw, way, stand);
        if (plan) plans.push({ rw, flow: way, stand, ...plan });
      }
      if (plans.some((p) => p.rw === rw)) break;
    }
  }
  if (plans.length === 0) return HoldReason.NoRoute;

  // Stand planning: passenger flights to a jet bridge (contact stand) when one is free, freight,
  // private jets and military aircraft to remote stands so the bridges stay free for passengers;
  // then the shortest taxi in.
  const wantsBridge =
    !def.military && f.cargo === 0 && f.cls !== AircraftClass.Light && f.cls !== AircraftClass.Ga;
  // A stand of its own size first (a small aircraft on a small one, a big one on a large one).
  const rank = (p: (typeof plans)[number]) =>
    standFit(p.stand.size, def) * STAND_MISMATCH * 2 +
    (p.stand.bridge === wantsBridge ? 0 : STAND_MISMATCH) +
    p.route.length;
  plans.sort((x, y) => rank(x) - rank(y) || x.stand.id - y.stand.id);

  // Earliest slot first: a later touchdown means a longer approach (holding).
  let reason: HoldReasonId = HoldReason.RunwayBusy;
  const free = (tile: number, b: Booking) => isFree(r.tiles, tile, b);
  for (const extra of HOLDING_TICKS) {
    const approach = APPROACH_TICKS + extra;
    for (const p of plans) {
      const t = arrivalTimes(w.time, approach, p.exit, r.sep);
      const slot = { ...t.runway, dir: p.flow, cat: WAKE_CATEGORY[f.cls] };
      if (!isFree(r.runways, p.rw.id, slot)) continue;
      if (!taxiBookings(p.route, 0, t.taxiStart, f.cls, airfield, free)) {
        reason = HoldReason.TaxiwayBusy;
        continue;
      }
      const aircraft: Aircraft = {
        id: w.nextOpsId++,
        flightId: f.id,
        cls: f.cls,
        airline: f.airline,
        phase: Phase.Approach,
        runwayId: p.rw.id,
        runwayDir: p.flow,
        runwayIndex: p.exit,
        runwayPos: 0,
        speed: 0,
        path: p.route,
        pathPos: 0,
        standId: p.stand.id,
        timer: approach,
        timerTotal: approach,
        svcLeft: new Array<number>(SERVICE_COUNT).fill(0),
        svcTotal: new Array<number>(SERVICE_COUNT).fill(0),
        boardState: 0,
        boardMilli: 0,
        fuelFromDepot: 0,
        gse: 0,
        contracted: 0,
        deiceLeft: 0,
        deiceTotal: 0,
        techLeft: 0,
        techTotal: 0,
        gseEta: new Array<number>(GSE_COUNT).fill(0),
        crewEta: 0,
        emergency: 0,
      };
      // Now and then a flight declares an emergency on its way in.
      const roll = w.rng.int(1000) < EMERGENCY_PERMILLE ? w.rng.int(10) : -1;
      // Falconers and bird scarers keep most birds away from the approach.
      const averted =
        roll >= 0 &&
        roll < 5 &&
        bestLevel(w, BuildingType.WildlifeControl) >= 1 &&
        w.rng.int(100) < WILDLIFE_AVERTED_PERCENT;
      if (roll >= 0 && !averted) {
        aircraft.emergency =
          roll < 5
            ? EmergencyKind.BirdStrike
            : roll < 8
              ? EmergencyKind.Medical
              : EmergencyKind.EngineFailure;
        w.emit({
          type: 'emergency',
          stage: 'declared',
          kind: aircraft.emergency as EmergencyKindId,
          flightId: f.id,
          airline: f.airline,
          runwayId: p.rw.id,
        });
      }
      w.aircraft.push(aircraft);
      f.lostArrival += Math.ceil(extra / TICKS_PER_GAME_MINUTE);
      r.stands.add(p.stand.id);
      book(r.runways, p.rw.id, slot);
      taxiBookings(p.route, 0, t.taxiStart, f.cls, airfield, (tile, b) => book(r.tiles, tile, b));
      f.aircraftId = aircraft.id;
      f.standId = p.stand.id;
      f.status = FlightStatus.Arriving;
      f.holdReason = HoldReason.None;
      return HoldReason.None;
    }
  }
  return reason;
}

// ---------------------------------------------------------------------------------------------
// Helicopters: straight onto a helipad and off it, no runway, no taxiing, no ground vehicles

/** Flying in from the edge of the area to over the pad, the descent onto it, the stop on the
 * pad at least, the lift off, and flying away (ticks). */
export const HELI_APPROACH_TICKS = 4 * TICKS_PER_GAME_MINUTE;
export const HELI_LAND_TICKS = TICKS_PER_GAME_MINUTE;
export const HELI_GROUND_MINUTES = 8;
export const HELI_LIFT_TICKS = TICKS_PER_GAME_MINUTE;
export const HELI_AWAY_TICKS = 3 * TICKS_PER_GAME_MINUTE;

/** A finished helipad nobody has taken, with a road to it (its passengers come by car). */
function tryArriveHelicopter(w: OpsWorld, f: Flight, r: Reservations): HoldReasonId {
  const airfield = w.airfield();
  const pad = w.buildings.find(
    (b) =>
      b.type === BuildingType.Helipad &&
      b.level >= 1 &&
      b.buildRemaining === 0 &&
      !r.stands.has(b.id) &&
      airfield.landsideConnected(b),
  );
  if (!pad) return HoldReason.NoHelipad;
  const aircraft: Aircraft = {
    id: w.nextOpsId++,
    flightId: f.id,
    cls: f.cls,
    airline: f.airline,
    phase: Phase.Approach,
    runwayId: 0,
    runwayDir: 0,
    runwayIndex: 0,
    runwayPos: 0,
    speed: 0,
    path: [],
    pathPos: 0,
    standId: pad.id,
    timer: HELI_APPROACH_TICKS,
    timerTotal: HELI_APPROACH_TICKS,
    svcLeft: new Array<number>(SERVICE_COUNT).fill(0),
    svcTotal: new Array<number>(SERVICE_COUNT).fill(0),
    boardState: 0,
    boardMilli: 0,
    fuelFromDepot: 0,
    gse: 0,
    contracted: 0,
    deiceLeft: 0,
    deiceTotal: 0,
    techLeft: 0,
    techTotal: 0,
    gseEta: new Array<number>(GSE_COUNT).fill(0),
    crewEta: 0,
    emergency: 0,
  };
  w.aircraft.push(aircraft);
  r.stands.add(pad.id);
  f.aircraftId = aircraft.id;
  f.standId = pad.id;
  f.status = FlightStatus.Arriving;
  f.holdReason = HoldReason.None;
  return HoldReason.None;
}

/** A helicopter's minute on its way: in, down onto its pad, a stop, up and away. */
function moveHelicopter(w: OpsWorld, a: Aircraft): boolean {
  const f = w.flights.find((x) => x.id === a.flightId);
  switch (a.phase) {
    case Phase.Approach:
      if (--a.timer <= 0) {
        a.phase = Phase.Landing;
        a.timer = HELI_LAND_TICKS;
        a.timerTotal = HELI_LAND_TICKS;
      }
      return true;
    case Phase.Landing:
      if (--a.timer <= 0) {
        a.phase = Phase.AtStand;
        a.timer = minutes(HELI_GROUND_MINUTES);
        a.timerTotal = a.timer;
        if (f) {
          f.status = FlightStatus.AtStand;
          w.emit({ type: 'flightArrived', flightId: f.id, airline: f.airline });
        }
      }
      return true;
    case Phase.AtStand:
      if (a.timer > 0) a.timer--;
      // Off at its time, once its passengers are out and the new ones (come by car) aboard.
      if (a.timer > 0 || (f && w.time < f.departAt)) return true;
      if (f) {
        f.paxArrived = f.paxOut;
        f.paxCheckedIn = f.paxOut;
        f.paxScreened = f.paxOut;
        f.paxReady = f.paxOut;
        f.paxBoarded = f.paxOut;
        f.gateClosed = 1;
        f.offBlockAt = w.time;
        f.status = FlightStatus.Departing;
      }
      a.phase = Phase.Takeoff;
      a.timer = HELI_LIFT_TICKS;
      a.timerTotal = HELI_LIFT_TICKS;
      return true;
    case Phase.Takeoff:
      if (--a.timer <= 0) {
        a.phase = Phase.Climb;
        a.timer = HELI_AWAY_TICKS;
        a.timerTotal = HELI_AWAY_TICKS;
        a.standId = 0;
      }
      return true;
    case Phase.Climb:
      if (--a.timer > 0) return true;
      departed(w, a);
      return false;
    default:
      return true;
  }
}

/** First reachable runway exit after the landing roll, and the taxi route from it. */
function arrivalRoute(airfield: Airfield, rw: RunwayInfo, flow: number, stand: StandInfo) {
  const dist = airfield.distancesTo(stand);
  const access = runwayAccess(rw, flow);
  for (const minIndex of [MIN_EXIT_INDEX, TOUCHDOWN_INDEX + 3]) {
    for (let i = minIndex; i < RUNWAY_LENGTH; i++) {
      const node = access[i] ?? -1;
      if (node < 0 || (dist[node] ?? -1) < 0) continue;
      const route = airfield.routeToStand(node, stand);
      if (route) return { exit: i, route };
    }
  }
  return null;
}

/** Runway entry giving the longest take-off run, and the taxi route to it from the stand. */
function departureRoute(airfield: Airfield, rw: RunwayInfo, flow: number, stand: StandInfo) {
  const dist = airfield.distancesTo(stand);
  const access = runwayAccess(rw, flow);
  for (let i = 0; i <= MAX_ENTRY_INDEX; i++) {
    const node = access[i] ?? -1;
    if (node < 0 || (dist[node] ?? -1) < 0) continue;
    const route = airfield.routeToStand(node, stand);
    if (route) return { entry: i, route: route.reverse() };
  }
  return null;
}

function tryDepart(
  w: OpsWorld,
  a: Aircraft,
  f: Flight | undefined,
  r: Reservations,
  fleet: Fleet,
): void {
  const airfield = w.airfield();
  const stand = airfield.stands.find((s) => s.id === a.standId);
  const onPad = stand?.kind === StandKind.Pad;
  if (a.deiceLeft > 0) {
    if (!onPad && hasPad(w, a.cls)) tryPad(w, a, f, r, fleet);
    return;
  }
  // Nose-in aircraft need a tug to push back (the contractor always has one): our own must be
  // free and have driven over to the aircraft. On the pad it drives straight through.
  const tug = onPad ? 0 : ownVehicles(fleet, gseBit(Gse.Tug));
  const choice = usableRunways(w, a.cls, RunwayMode.Arrivals);
  if (choice.usable.length === 0) {
    // No runway to go to (night on an unlit one, wind, fog, works…): the tug is not held for
    // it, or one waiting aircraft would keep every other departure at its stand.
    if (tug && a.gse & tug) release(fleet, a, a.gse & ~tug);
    if (f) f.holdReason = choice.reason;
    return;
  }
  if (
    tug &&
    !((a.gse & tug) !== 0
      ? arrived(a, tug)
      : acquire(fleet, a, tug, (k) => vehicleTravel(w, a, k)) && arrived(a, tug))
  ) {
    if (f) f.holdReason = HoldReason.NoTug;
    return;
  }
  let reason: HoldReasonId = choice.usable.length === 0 ? choice.reason : HoldReason.NoRoute;
  const free = (tile: number, b: Booking) => isFree(r.tiles, tile, b);
  if (stand) {
    for (const usable of choice.usable) {
      const rw = usable.rw;
      let flow = usable.flow;
      let plan = null;
      for (const way of contraFlow(w, rw, usable.flow)) {
        plan = departureRoute(airfield, rw, way, stand);
        flow = way;
        if (plan) break;
      }
      if (!plan) continue;
      const runway = {
        ...departureRunway(w.time, plan.route, 0, plan.entry, r.sep),
        dir: flow,
        cat: WAKE_CATEGORY[a.cls],
      };
      if (!isFree(r.runways, rw.id, runway)) {
        if (reason === HoldReason.NoRoute) reason = HoldReason.RunwayBusy;
        continue;
      }
      if (!taxiBookings(plan.route, 0, w.time, a.cls, airfield, free)) {
        reason = HoldReason.TaxiwayBusy;
        continue;
      }
      a.phase = Phase.TaxiOut;
      a.runwayId = rw.id;
      a.runwayDir = flow;
      a.runwayIndex = plan.entry;
      a.path = plan.route;
      a.pathPos = 0;
      book(r.runways, rw.id, runway);
      taxiBookings(plan.route, 0, w.time, a.cls, airfield, (tile, b) => book(r.tiles, tile, b));
      if (f) {
        f.holdReason = HoldReason.None;
        // Off its stand now (from the de-icing pad it already was).
        if (!onPad) leaveStand(w, f);
      }
      return;
    }
  }
  if (f) f.holdReason = reason;
}

/** Services that follow the arrival (the rest wait for the departure after a long stay). */
const ARRIVAL_SERVICES: ReadonlySet<number> = new Set([
  Service.Deboard,
  Service.Unload,
  Service.Clean,
]);
/**
 * Tiles per tick under tow: as fast as taxiing, the pace its taxiway bookings are made at (at
 * half of it the tow ran past its bookings, and a long way to a remote position took hours).
 */
const TOW_SPEED = TAXI_SPEED;

/** Minutes before boarding within which the stairs stay at an aircraft after deboarding. */
const STAIRS_STAY_MINUTES = 20;

/** A night stop or rental still well before its morning: it may stand on a remote position. */
function beforeTowBack(w: OpsWorld, f: Flight): boolean {
  return f.park !== Stay.Turnaround && w.time < f.departAt - minutes(TOW_BACK_MINUTES);
}

/** At its stand, the arrival work done, and long enough to wait that a tow pays. */
function parkingDue(w: OpsWorld, a: Aircraft, f: Flight): boolean {
  if (!beforeTowBack(w, f)) return false;
  if (f.departAt - minutes(TOW_BACK_MINUTES) - w.time < minutes(PARK_MIN_MINUTES)) return false;
  for (const i of ARRIVAL_SERVICES) if ((a.svcLeft[i] ?? 0) > 0) return false;
  return w.time % TICKS_PER_GAME_MINUTE === 0;
}

/**
 * Tows the aircraft to a free stand of `kind` (a remote position, or back to a passenger stand
 * that suits it): a tug hitched on, the route booked like a taxi at walking pace.
 */
function tryTow(w: OpsWorld, a: Aircraft, f: Flight, fleet: Fleet, kind: StandKindId): void {
  const airfield = w.airfield();
  const from = airfield.stands.find((s) => s.id === a.standId);
  if (!from) return;
  const r = reservations(w);
  // Back to a stand: the one held for it (see holdStandsForTowBack).
  const held = kind === StandKind.Civil ? r.held.get(a.id) : undefined;
  const heldStand = held === undefined ? undefined : airfield.stands.find((s) => s.id === held);
  const targets = heldStand ? [heldStand] : towTargets(w, a, kind, r);
  const tug = ownVehicles(fleet, gseBit(Gse.Tug));
  if (targets.length === 0) {
    // Nowhere to go now: the tug called for it goes back meanwhile.
    if (a.gse & tug) release(fleet, a, a.gse & ~tug);
    return;
  }
  if (
    tug &&
    !((a.gse & tug) !== 0
      ? arrived(a, tug)
      : acquire(fleet, a, tug, (k) => vehicleTravel(w, a, k)) && arrived(a, tug))
  ) {
    return;
  }
  const free = (tile: number, b: Booking) => isFree(r.tiles, tile, b);
  for (const to of targets) {
    for (const entry of from.entries) {
      const out = airfield.routeToStand(entry, from);
      const on = airfield.routeToStand(entry, to);
      if (!out || !on) continue;
      const route = [...out.reverse(), ...on.slice(1)];
      if (!taxiBookings(route, 0, w.time, a.cls, airfield, free)) continue;
      // The tug stays hitched for the tow; everything else goes back.
      release(fleet, a, tug);
      a.phase = Phase.Tow;
      a.path = route;
      a.pathPos = 0;
      a.standId = to.id;
      if (kind === StandKind.Civil) f.standId = to.id;
      return;
    }
  }
}

/** Off-block: the flight leaves its stand (for the runway, or the de-icing pad first). */
function leaveStand(w: OpsWorld, f: Flight): void {
  f.status = FlightStatus.Departing;
  f.offBlockAt = w.time;
  propagateDelay(w, f);
}

/** Whether the aircraft stands on a de-icing pad (no pushback from there). */
function onPadNow(w: OpsWorld, a: Aircraft): boolean {
  const type = w.buildings.find((b) => b.id === a.standId)?.type;
  return type === BuildingType.DeicePad || type === BuildingType.LargeDeicePad;
}

/** Whether a de-icing pad (of `type`) takes an aircraft class: the big ones need a large pad. */
export function padTakes(type: number, cls: AircraftClassId): boolean {
  if (type === BuildingType.LargeDeicePad) return true;
  return type === BuildingType.DeicePad && AIRCRAFT_CLASSES[cls].standSize !== Size.Large;
}

/** A finished de-icing pad the class fits. */
function hasPad(w: OpsWorld, cls: AircraftClassId): boolean {
  return w.buildings.some((b) => padTakes(b.type, cls) && b.buildRemaining === 0);
}

/**
 * Pushes back from the stand and taxies to a free de-icing pad (the stand is free for the next
 * arrival at once). Waits at the stand while every pad is taken or no way there is clear.
 */
function tryPad(
  w: OpsWorld,
  a: Aircraft,
  f: Flight | undefined,
  r: Reservations,
  fleet: Fleet,
): void {
  if (f) f.holdReason = HoldReason.Deicing;
  const tug = ownVehicles(fleet, gseBit(Gse.Tug));
  if (
    tug &&
    !((a.gse & tug) !== 0
      ? arrived(a, tug)
      : acquire(fleet, a, tug, (k) => vehicleTravel(w, a, k)) && arrived(a, tug))
  ) {
    return;
  }
  const airfield = w.airfield();
  const stand = airfield.stands.find((s) => s.id === a.standId);
  if (!stand) return;
  const free = (tile: number, b: Booking) => isFree(r.tiles, tile, b);
  for (const pad of airfield.stands) {
    if (pad.kind !== StandKind.Pad || r.stands.has(pad.id)) continue;
    const building = w.buildings.find((b) => b.id === pad.id);
    if (!building || building.buildRemaining > 0 || !padTakes(building.type, a.cls)) continue;
    // Out of the stand to one of its taxiway entries, and from there onto the pad.
    for (const entry of stand.entries) {
      const out = airfield.routeToStand(entry, stand);
      const on = airfield.routeToStand(entry, pad);
      if (!out || !on) continue;
      const route = [...out.reverse(), ...on.slice(1)];
      if (!taxiBookings(route, 0, w.time, a.cls, airfield, free)) continue;
      a.phase = Phase.TaxiOut;
      a.path = route;
      a.pathPos = 0;
      a.standId = pad.id;
      r.stands.add(pad.id);
      taxiBookings(route, 0, w.time, a.cls, airfield, (tile, b) => book(r.tiles, tile, b));
      if (f) leaveStand(w, f);
      return;
    }
  }
}

function finishFlight(w: OpsWorld, f: Flight, status: FlightStatusId): void {
  f.status = status;
  f.finishedAt = w.time;
  f.aircraftId = 0;
}

// ---------------------------------------------------------------------------------------------
// Movement

/**
 * A landing or take-off at night counts against the noise quota; every point beyond it is fined
 * and costs the airport goodwill with its neighbours.
 */
function noise(w: OpsWorld, a: Aircraft): void {
  if (!isNight(w.time)) return;
  const quota = noiseQuota(towerLevel(w.buildings), w.noiseInsulation);
  const before = Math.max(0, w.nightNoise - quota);
  w.nightNoise += NOISE_POINTS[a.cls];
  const over = Math.max(0, w.nightNoise - quota) - before;
  if (over === 0) return;
  const fine = over * NOISE_FINE_PER_POINT;
  w.money -= fine;
  w.today.noiseFines += fine;
  talk(w, -over * NOISE_BUZZ_PER_POINT);
  w.emit({ type: 'noiseComplaint', flightId: a.flightId, points: w.nightNoise, fine });
}

/**
 * A touchdown wears the runway. On a worn one, debris may cut a tyre: the aircraft needs a tyre
 * change at the stand (carried there in techLeft, see the turnaround set-up) and the airport
 * pays the airline's claim.
 */
function touchdownWear(w: OpsWorld, a: Aircraft): void {
  const b = w.buildings.find((x) => x.id === a.runwayId);
  if (!b) return;
  const before = b.wear;
  b.wear = Math.min(WEAR_MAX, before + (WEAR_PER_LANDING[a.cls] ?? 1));
  // The surface shows on the map in steps of a tenth.
  if (Math.floor(before / 100) !== Math.floor(b.wear / 100)) w.buildingsDirty = true;
  const crossed = (limit: number) => before < limit && b.wear >= limit;
  if (crossed(WEAR_WORN) || crossed(WEAR_POOR)) {
    w.emit({
      type: 'runwayWear',
      stage: crossed(WEAR_POOR) ? 'poor' : 'worn',
      runwayId: b.id,
      flightId: a.flightId,
    });
  }
  const chance = fodPermille(b.wear);
  if (chance > 0 && w.rng.int(1000) < chance) {
    a.techLeft += minutes(FOD_REPAIR_MINUTES);
    w.money -= FOD_CLAIM;
    w.today.penalties += FOD_CLAIM;
    w.emit({ type: 'runwayWear', stage: 'fod', runwayId: b.id, flightId: a.flightId });
  }
}

/** Go-around chance (per mille) for this arrival in the weather now. */
function goAroundPermille(w: OpsWorld, a: Aircraft): number {
  let p = GO_AROUND_PERMILLE;
  if (w.sky === Sky.Rain) p += 8;
  if (w.sky === Sky.Snow) p += 20;
  if (w.sky === Sky.Storm) p += 40;
  // Blowing sand: gusts and the runway lost in the haze on short final.
  if (w.sky === Sky.Dust) p += 25;
  const b = w.buildings.find((x) => x.id === a.runwayId);
  if (b) {
    // Close to the minimum the pilots often do not see the runway lights in time.
    if (w.visibility * 2 < visibilityMinimum(b.level) * 3) p += 25;
    const limit = AIRCRAFT_CLASSES[a.cls].crosswindLimit;
    const cross = crosswind(w.windDir, w.windSpeed, runwayHeading(b.rot, a.runwayDir));
    if (cross * 10 > limit * 7) p += 30;
  }
  return p;
}

/**
 * The arrival goes around: it climbs away over the runway and flies the circuit back to the
 * start of the final. The tower gives it the first slot after the circuit that is clear of all
 * other traffic (its stand and taxi route stay its own); with none within half an hour it is
 * diverted to another airport. False when the aircraft leaves (diverted).
 */
function goAround(w: OpsWorld, a: Aircraft): boolean {
  const f = w.flights.find((x) => x.id === a.flightId);
  // Everyone else's bookings, without its own touchdown that is not happening now.
  const at = w.aircraft.indexOf(a);
  w.aircraft.splice(at, 1);
  const r = reservations(w);
  w.aircraft.splice(at, 0, a);
  const airfield = w.airfield();
  const free = (tile: number, b: Booking) => isFree(r.tiles, tile, b);
  for (const extra of GO_AROUND_HOLDING) {
    const t = arrivalTimes(w.time, GO_AROUND_TICKS + extra + APPROACH_TICKS, a.runwayIndex, r.sep);
    const slot = { ...t.runway, dir: a.runwayDir, cat: WAKE_CATEGORY[a.cls] };
    if (!isFree(r.runways, a.runwayId, slot)) continue;
    if (!taxiBookings(a.path, 0, t.taxiStart, a.cls, airfield, free)) continue;
    a.phase = Phase.GoAround;
    a.timer = GO_AROUND_TICKS + extra;
    a.timerTotal = a.timer;
    if (f) f.lostArrival += Math.ceil((GO_AROUND_TICKS + extra) / TICKS_PER_GAME_MINUTE);
    w.emit({
      type: 'goAround',
      flightId: a.flightId,
      airline: a.airline,
      runwayId: a.runwayId,
      diverted: false,
    });
    return true;
  }
  w.emit({
    type: 'goAround',
    flightId: a.flightId,
    airline: a.airline,
    runwayId: a.runwayId,
    diverted: true,
  });
  if (f) {
    f.holdReason = HoldReason.Diverted;
    cancel(w, f);
  }
  return false;
}

function moveAircraft(w: OpsWorld, a: Aircraft, fleet: Fleet): boolean {
  switch (a.phase) {
    case Phase.Approach:
      if (--a.timer <= 0) {
        if (!a.emergency && w.rng.int(1000) < goAroundPermille(w, a)) return goAround(w, a);
        a.phase = Phase.Landing;
        a.runwayPos = TOUCHDOWN_INDEX * 1000;
        a.speed = LANDING_SPEED;
        noise(w, a);
        touchdownWear(w, a);
      }
      return true;
    case Phase.Landing:
      a.runwayPos += a.speed;
      a.speed = rollout(a.speed, a.runwayIndex * 1000 - a.runwayPos);
      if (a.runwayPos >= a.runwayIndex * 1000) {
        a.runwayPos = a.runwayIndex * 1000;
        a.phase = Phase.TaxiIn;
        a.pathPos = 0;
        a.speed = TAXI_SPEED;
        if (a.emergency && a.emergency !== EmergencyKind.Medical) emergencyLanded(w, a);
      }
      return true;
    case Phase.TaxiIn:
    case Phase.TaxiOut: {
      const end = (a.path.length - 1) * 1000;
      a.pathPos = Math.min(end, a.pathPos + TAXI_SPEED);
      // Pushback done: the tug goes back to the base.
      if (a.gse && a.pathPos >= PUSHBACK_TILES * 1000) release(fleet, a, 0);
      if (a.pathPos < end) return true;
      if (a.phase === Phase.TaxiIn) {
        a.phase = Phase.AtStand;
        const f = w.flights.find((x) => x.id === a.flightId);
        startTurnaround(w, a, f);
        if (f) {
          f.status = FlightStatus.AtStand;
          // Its connecting passengers head for their next flight.
          if (f.transferIn > 0) w.transferPool.push({ at: w.time, n: f.transferIn });
          w.emit({ type: 'flightArrived', flightId: f.id, airline: f.airline });
        }
      } else if (a.deiceLeft > 0) {
        // On the de-icing pad.
        a.phase = Phase.Deicing;
      } else {
        a.phase = Phase.Takeoff;
        a.runwayPos = a.runwayIndex * 1000;
        a.speed = TAKEOFF_START_SPEED;
        a.standId = 0; // The stand is released once the aircraft is on the runway.
      }
      return true;
    }
    case Phase.AtStand: {
      const f = w.flights.find((x) => x.id === a.flightId);
      // A long stay: once the arrival work is done, towed off to a remote position.
      if (f && parkingDue(w, a, f)) {
        tryTow(w, a, f, fleet, StandKind.Remote);
        if ((a.phase as number) === Phase.Tow) return true;
      }
      turnaround(w, a, fleet);
      return true;
    }
    case Phase.Tow: {
      const end = (a.path.length - 1) * 1000;
      a.pathPos = Math.min(end, a.pathPos + TOW_SPEED);
      if (a.pathPos < end) return true;
      // The tug goes home; the aircraft waits on its position, or carries on at its stand.
      release(fleet, a, 0);
      const there = w.airfield().stands.find((s) => s.id === a.standId);
      a.phase = there?.kind === StandKind.Remote ? Phase.Parked : Phase.AtStand;
      return true;
    }
    case Phase.Parked: {
      // A tug called for the tow back drives over meanwhile (without this it never got there,
      // and the aircraft stood on its position for days, holding the tug).
      driveOver(a);
      const f = w.flights.find((x) => x.id === a.flightId);
      if (!f) {
        a.phase = Phase.Ready;
        return true;
      }
      if (f.park === Stay.Rental) {
        // A rental has nobody to board: it leaves from its position when its days are up.
        if (w.time >= f.departAt) {
          a.phase = Phase.Ready;
          f.holdReason = HoldReason.None;
        }
        return true;
      }
      if (!beforeTowBack(w, f)) {
        f.holdReason = HoldReason.NoStand;
        tryTow(w, a, f, fleet, StandKind.Civil);
        if ((a.phase as number) === Phase.Tow) f.holdReason = HoldReason.None;
        else if (w.time >= f.departAt && w.time % TICKS_PER_GAME_MINUTE === 0) f.lostDeparture++;
      }
      return true;
    }
    case Phase.Ready: {
      const f = w.flights.find((x) => x.id === a.flightId);
      driveOver(a);
      const travel = (k: number) => vehicleTravel(w, a, k);
      const padNext = a.deiceLeft > 0 && hasPad(w, a.cls);
      if (padNext) {
        // De-iced on the pad on the way out: called by tryDepart once a pad is free.
        const tug = ownVehicles(fleet, gseBit(Gse.Tug));
        if (tug) acquire(fleet, a, tug, travel);
        if (f) f.holdReason = HoldReason.Deicing;
      } else if (a.deiceLeft > 0) {
        // The airport's own de-icing truck if it has any, else the contractor (no vehicle).
        const truck = (fleet.total[Gse.Deicer] ?? 0) > 0 ? gseBit(Gse.Deicer) : 0;
        if (acquire(fleet, a, truck, travel) && arrived(a, truck)) {
          a.deiceLeft--;
          if (a.deiceLeft === 0) release(fleet, a, a.gse & gseBit(Gse.Tug));
        }
        if (f) f.holdReason = HoldReason.Deicing;
      } else if (!onPadNow(w, a)) {
        // Call the pushback tug while waiting, so it is there when the slot comes.
        const tug = ownVehicles(fleet, gseBit(Gse.Tug));
        if (tug) acquire(fleet, a, tug, travel);
      }
      // Ready to go but held (de-icing, runway, taxiway, tug, night, wind, fog).
      if (f && w.time % TICKS_PER_GAME_MINUTE === 0) f.lostDeparture++;
      return true;
    }
    case Phase.Deicing: {
      // The pad's own gantries and crews: no vehicles needed. Then on to the runway from here.
      const f = w.flights.find((x) => x.id === a.flightId);
      if (a.deiceLeft > 0) a.deiceLeft--;
      if (a.deiceLeft === 0) a.phase = Phase.Ready;
      if (f) {
        f.holdReason = HoldReason.Deicing;
        if (w.time % TICKS_PER_GAME_MINUTE === 0) f.lostDeparture++;
      }
      return true;
    }
    case Phase.Takeoff:
      a.speed += TAKEOFF_ACCEL;
      a.runwayPos += a.speed;
      if (a.speed >= LIFTOFF_SPEED || a.runwayPos >= (RUNWAY_LENGTH - 1) * 1000) {
        noise(w, a);
        a.phase = Phase.Climb;
        a.timer = CLIMB_TICKS;
        a.timerTotal = CLIMB_TICKS;
      }
      return true;
    case Phase.Climb:
      if (--a.timer > 0) return true;
      departed(w, a);
      return false;
    case Phase.GoAround:
      // Round the circuit and onto the final again, for the slot booked when it went around.
      if (--a.timer <= 0) {
        a.phase = Phase.Approach;
        a.timer = APPROACH_TICKS;
        a.timerTotal = APPROACH_TICKS;
      }
      return true;
    default:
      return true;
  }
}

// ---------------------------------------------------------------------------------------------
// Turnaround: ground services and boarding

/** The airport handles aircraft itself: it has a base and crews enough to run it. */
function ownHandling(w: OpsWorld): boolean {
  return (
    servedLevel(w, BuildingType.GroundHandling) >= 1 &&
    staffing(w, StaffRole.Ground) >= GROUND_CONTRACT_BELOW
  );
}

/**
 * Best level among buildings of `type` whose vehicles can get out: the ground handling base and
 * the fuel depot only work with a road to the airport's road network.
 */
function servedLevel(w: OpsWorld, type: number): number {
  const airfield = w.airfield();
  let best = 0;
  // The airside's own bases are reached by service roads too; the rest by the public roads.
  const reached = SERVICE_SIDE.has(type)
    ? (b: Building) => airfield.serviceConnected(b)
    : (b: Building) => airfield.landsideConnected(b);
  for (const b of w.buildings) {
    if (b.type === type && b.level > best && reached(b)) best = b.level;
  }
  return best;
}

/** Buildings the airport's own vehicles serve or come from: a service road reaches them. */
export const SERVICE_SIDE: ReadonlySet<number> = new Set<number>([
  BuildingType.MilitaryGate,
  BuildingType.Stand,
  BuildingType.ContactStand,
  BuildingType.GroundHandling,
  BuildingType.FuelDepot,
  BuildingType.FireStation,
  BuildingType.CateringKitchen,
  BuildingType.Hangar,
  BuildingType.Shelter,
  BuildingType.MilitaryApron,
  BuildingType.DeicePad,
  BuildingType.LargeDeicePad,
  BuildingType.Directorate,
  BuildingType.RemoteParking,
  BuildingType.SmallStand,
  BuildingType.LargeStand,
  BuildingType.SmallParking,
  BuildingType.LargeParking,
]);

/**
 * Ticks a vehicle of `kind` needs to drive to the aircraft's stand: from the nearest base that
 * can send it (the fuel depot for fuel trucks, the ground handling base for the rest), or from
 * outside the airport over the motorway when no base can.
 */
function vehicleTravel(w: OpsWorld, a: Aircraft, kind: number): number {
  const airfield = w.airfield();
  const stand = w.buildings.find((b) => b.id === a.standId);
  if (!stand) return 0;
  const home = kind === Gse.Fuel ? BuildingType.FuelDepot : BuildingType.GroundHandling;
  let best = -1;
  for (const b of w.buildings) {
    if (b.type !== home || b.level < 1) continue;
    const d = airfield.serviceTicks(b, stand);
    if (d >= 0 && (best < 0 || d < best)) best = d;
  }
  if (best < 0) best = Math.max(0, airfield.serviceTicks(null, stand));
  return best;
}

/**
 * Level of the best transit hub that works: on a road to the motorway and close to a connected
 * terminal (passengers walk over from it).
 */
function transitLevel(w: OpsWorld, type: number = BuildingType.TransitHub): number {
  const airfield = w.airfield();
  const terminals = w.buildings.filter(
    (b) => b.type === BuildingType.Terminal && b.level >= 1 && airfield.landsideConnected(b),
  );
  let best = 0;
  for (const b of w.buildings) {
    if (b.type !== type || b.level <= best) continue;
    if (!airfield.landsideConnected(b)) continue;
    const near = terminals.some((t) => Math.abs(t.x - b.x) + Math.abs(t.y - b.y) <= TRANSIT_REACH);
    if (near) best = b.level;
  }
  return best;
}

/** Buildings whose vehicles take part in operations (see servedLevel). */
export function servingBuildings(w: OpsWorld): Building[] {
  const airfield = w.airfield();
  return w.buildings.filter(
    (b) =>
      (b.type !== BuildingType.GroundHandling && b.type !== BuildingType.FuelDepot) ||
      airfield.serviceConnected(b),
  );
}

/**
 * An hour of winter service (see snow.ts): snow settles on every runway while it snows; the
 * crews' routine ploughing takes some off each open one; a runway it buries closes until the
 * plough team comes.
 */
function winterService(w: OpsWorld): void {
  const fall = w.sky === Sky.Snow ? (SNOW_PER_HOUR[w.snowfall] ?? 0) : 0;
  const base = Math.min(4, servedLevel(w, BuildingType.GroundHandling));
  const routine = SNOW_CLEARING[base] ?? 6;
  let working = false;
  for (const rw of w.buildings) {
    if (!isRunway(rw.type) || rw.level < 1) continue;
    const before = snowDepth(w.runwaySnow, rw.id);
    if (before === 0 && fall === 0) continue;
    const closed = w.runwayClosures.some(
      (c) => c.runwayId === rw.id && c.kind === ClosureKind.Snow && c.from <= w.time,
    );
    // Ploughs on their way: they will take it all.
    const sent = w.snowJobs.some((j) => j.runwayId === rw.id);
    let depth = Math.min(100, before + fall);
    if (!closed && !sent) {
      working = true;
      depth = Math.max(0, depth - routine);
    }
    setSnowDepth(w.runwaySnow, rw.id, depth);
    if (closed || sent) continue;
    if (depth >= SNOW_CLOSE) {
      w.runwayClosures.push({
        runwayId: rw.id,
        from: w.time,
        until: w.time + SNOW_WAIT,
        kind: ClosureKind.Snow,
      });
      w.emit({ type: 'snow', stage: 'closed', runwayId: rw.id });
    } else if (before < SNOW_WARN && depth >= SNOW_WARN) {
      w.emit({ type: 'snow', stage: 'deep', runwayId: rw.id });
    }
  }
  // Without a base of its own the airport pays a contractor for every hour of it.
  if (working && base === 0) {
    w.money -= SNOW_CONTRACTOR_FEE;
    w.today.handling += SNOW_CONTRACTOR_FEE;
  }
}

/**
 * The winter's blizzard (see snow.ts): planned on the first morning of winter for one of the
 * days after, forecast hours ahead, and when its time comes (once it is cold enough to snow) it
 * snows as hard as it can for a few hours.
 */
function blizzard(w: OpsWorld, hour: number): void {
  // The desert has none.
  if (!hasBlizzards(w.theme)) {
    w.blizzardAt = 0;
    w.blizzardUntil = 0;
    return;
  }
  const H = TICKS_PER_GAME_HOUR;
  const winter = season(w.time) === Season.Winter;
  if (hour === 0 && winter && dayIndex(w.time) % DAYS_PER_SEASON === 0) {
    const day = BLIZZARD_FIRST_DAY + w.rng.int(BLIZZARD_DAYS);
    w.blizzardAt = w.time + day * TICKS_PER_GAME_DAY + w.rng.int(24) * H;
  }
  // A winter too mild for it has none.
  if (!winter) w.blizzardAt = 0;
  if (w.blizzardAt > 0 && w.blizzardAt - w.time === BLIZZARD_WARNING) {
    w.emit({ type: 'blizzard', stage: 'warning' });
  }
  if (w.blizzardAt > 0 && w.time >= w.blizzardAt) {
    if (temperatureAt(w.climate, hour, w.theme) <= 1) {
      w.blizzardUntil = w.time + (BLIZZARD_MIN_HOURS + w.rng.int(BLIZZARD_HOURS_SPAN)) * H;
      w.blizzardAt = 0;
      w.emit({ type: 'blizzard', stage: 'started' });
    } else {
      // Not cold enough yet: it waits for the night.
      w.blizzardAt = w.time + H;
    }
  }
  if (w.time < w.blizzardUntil) {
    w.sky = Sky.Snow;
    w.snowfall = Snowfall.Blizzard;
    w.visibility = 2 + w.rng.int(4);
  } else if (w.blizzardUntil > 0) {
    w.blizzardUntil = 0;
    // It eases off rather than stopping at once.
    if (w.sky === Sky.Snow) w.snowfall = Snowfall.Moderate;
    w.emit({ type: 'blizzard', stage: 'ended' });
  }
  // The forecast knows: snow through a blizzard under way, or on its way (once cold enough).
  for (let i = 0; i < w.skyAhead.length; i++) {
    const at = w.time + (i + 1) * H;
    const coming =
      w.blizzardAt > 0 &&
      at >= w.blizzardAt &&
      at < w.blizzardAt + BLIZZARD_MIN_HOURS * H &&
      temperatureAt(w.climate, (hour + i + 1) % 24, w.theme) <= 1;
    if (coming || at < w.blizzardUntil) w.skyAhead[i] = Sky.Snow;
  }
}

/** Strikes: underpaid staff may walk out in the morning; they come back after a few hours. */
function labourRelations(w: OpsWorld, hour: number): void {
  if (w.strikeUntil > 0 && w.time >= w.strikeUntil) {
    w.strikeUntil = 0;
    w.emit({ type: 'strike', stage: 'ended' });
  }
  const chance = strikeChance(w.wagePct);
  if (hour === 7 && w.strikeUntil === 0 && chance > 0 && w.rng.int(1000) < chance) {
    w.strikeUntil = w.time + STRIKE_HOURS * TICKS_PER_GAME_HOUR;
    talk(w, -60);
    w.emit({ type: 'strike', stage: 'started' });
  }
}

/** Share (percent) of arriving passengers who connect here: grows with the route network. */
function transferPercent(w: OpsWorld): number {
  const routes = w.contracts.filter((c) => !c.cargo).length;
  if (routes < 2) return 0;
  return Math.min(TRANSFER_PERCENT_MAX, routes * TRANSFER_PERCENT_PER_ROUTE);
}

/**
 * Connecting passengers who have made the minimum connection time board a departure, oldest
 * arrivals first, up to the empty seats. Returns how many.
 */
function boardTransfers(w: OpsWorld, seats: number): number {
  const mct = minutes(MIN_CONNECTION_MINUTES[bestLevel(w, BuildingType.Terminal)] ?? 60);
  let taken = 0;
  const patience = minutes(TRANSFER_WINDOW_MINUTES);
  for (const g of w.transferPool) {
    if (taken >= seats || g.at + mct > w.time) break;
    // Past the window they have missed their connection (counted on the hour).
    if (g.at + patience < w.time) continue;
    const n = Math.min(g.n, seats - taken);
    g.n -= n;
    taken += n;
  }
  while (w.transferPool.length > 0 && (w.transferPool[0] as TransferGroup).n === 0) {
    w.transferPool.shift();
  }
  return taken;
}

/** Connecting passengers who found no seat in time: rebooked and compensated, and unhappy. */
function missConnections(w: OpsWorld): void {
  let missed = 0;
  const patience = minutes(TRANSFER_WINDOW_MINUTES);
  while (w.transferPool.length > 0 && (w.transferPool[0] as TransferGroup).at + patience < w.time) {
    missed += (w.transferPool.shift() as TransferGroup).n;
  }
  if (missed === 0) return;
  const cost = missed * MISSED_CONNECTION_COST;
  w.money -= cost;
  w.today.penalties += cost;
  w.today.missedConnections += missed;
  talk(w, -Math.min(80, missed));
}

/** Bags lost on a departure of `flown` passengers (one bag each). */
function lostBags(w: OpsWorld, a: Aircraft, flown: number): number {
  const crews = staffing(w, StaffRole.Ground);
  const permille = a.contracted
    ? BAGS_LOST_CONTRACTOR_PERMILLE
    : BAGS_LOST_PERMILLE + Math.floor((1000 - Math.min(1000, crews)) / 50);
  const expected = flown * permille;
  return Math.floor(expected / 1000) + (w.rng.int(1000) < expected % 1000 ? 1 : 0);
}

/**
 * Spotters' decks (spotters.ts) overlooking the runway an aircraft left from: what their
 * visitors pay for the departure, in daylight (and they talk about it).
 */
export function spotterIncome(w: OpsWorld, a: Pick<Aircraft, 'runwayId' | 'cls'>): number {
  if (!a.runwayId || isNight(w.time)) return 0;
  const runway = w.buildings.find((b) => b.id === a.runwayId);
  if (!runway) return 0;
  const rw = footprint(BUILDINGS[runway.type], runway.rot);
  const rx = runway.x * 2 + rw.w;
  const ry = runway.y * 2 + rw.h;
  const airfield = w.airfield();
  const military = AIRCRAFT_CLASSES[a.cls].military;
  let income = 0;
  for (const b of w.buildings) {
    if (b.type !== BuildingType.SpotterDeck || b.level < 1 || b.buildRemaining > 0) continue;
    const d = footprint(BUILDINGS[b.type], b.rot);
    // Half-tiles: no fractions.
    const dx = Math.abs(b.x * 2 + d.w - rx);
    const dy = Math.abs(b.y * 2 + d.h - ry);
    // Along a long runway the deck sees it from anywhere beside it.
    const reachX = SPOTTER_RANGE * 2 + rw.w;
    const reachY = SPOTTER_RANGE * 2 + rw.h;
    if (dx > reachX || dy > reachY || !airfield.landsideConnected(b)) continue;
    income += spotterFee(b.level, a.cls, !!military);
  }
  if (income > 0) talk(w, SPOTTER_BUZZ);
  return income;
}

/** Rooms across the hotels on a road. */
function hotelRooms(w: OpsWorld): number {
  const airfield = w.airfield();
  let rooms = 0;
  for (const b of w.buildings) {
    if (b.type !== BuildingType.Hotel || b.level < 1 || !airfield.landsideConnected(b)) continue;
    rooms += HOTEL_ROOMS[b.level] ?? 0;
  }
  return rooms;
}

/**
 * One night at the hotels: passengers on early flights and connections, and the crews of the
 * airlines flying here, fill rooms up to what there is; each room pays its hotel's rate (the
 * better hotels are filled first).
 */
function hotelNight(w: OpsWorld): void {
  const airfield = w.airfield();
  const hotels = w.buildings
    .filter((b) => b.type === BuildingType.Hotel && b.level >= 1 && airfield.landsideConnected(b))
    .sort((a, b) => b.level - a.level || a.id - b.id);
  if (hotels.length === 0) return;
  let guests =
    Math.floor((w.yesterday.passengers * HOTEL_GUESTS_PERMILLE) / 1000) +
    w.contracts.length * HOTEL_CREW_ROOMS;
  let revenue = 0;
  let stayed = 0;
  for (const h of hotels) {
    const n = Math.min(guests, HOTEL_ROOMS[h.level] ?? 0);
    revenue += n * (HOTEL_ROOM_RATE[h.level] ?? 0);
    stayed += n;
    guests -= n;
  }
  w.money += revenue;
  w.today.hotel += revenue;
  w.today.hotelGuests += stayed;
}

/** The most of a facility any terminal on a road has. */
function terminalFacility(w: OpsWorld, id: FacilityId): number {
  const airfield = w.airfield();
  let best = 0;
  for (const b of w.buildings) {
    if (b.type !== BuildingType.Terminal || !airfield.landsideConnected(b)) continue;
    best = Math.max(best, facilityCount(b.facilities, id));
  }
  return best;
}

/** Best level among terminals passengers can reach by road. */
function terminalLevel(w: OpsWorld): number {
  return servedLevel(w, BuildingType.Terminal);
}

function bestLevel(w: OpsWorld, type: number): number {
  let best = 0;
  for (const b of w.buildings) if (b.type === type && b.level > best) best = b.level;
  return best;
}

/** Whether passengers board at this stand by jet bridge (a contact stand). */
function bridged(w: OpsWorld, standId: number): boolean {
  return w.buildings.some((b) => b.id === standId && b.type === BuildingType.ContactStand);
}

/** A level 2 stand: quicker ground services and two sets of stairs (no bridge). */
function fastStand(w: OpsWorld, standId: number): boolean {
  return w.buildings.some(
    (b) =>
      b.id === standId &&
      ((b.type === BuildingType.Stand && b.level >= 2) || b.type === BuildingType.LargeStand),
  );
}

/** Passengers per minute through the doors at this stand. */
function doorRate(w: OpsWorld, standId: number): number {
  if (bridged(w, standId)) return BRIDGE_PAX_PER_MINUTE;
  return fastStand(w, standId) ? FAST_STAND_PAX_PER_MINUTE : REMOTE_PAX_PER_MINUTE;
}

const minutes = (m: number) => m * TICKS_PER_GAME_MINUTE;

/** Plans the ground services when an aircraft reaches its stand. */
function startTurnaround(w: OpsWorld, a: Aircraft, f: Flight | undefined): void {
  const def = AIRCRAFT_CLASSES[a.cls];
  const paxIn = f?.paxIn ?? 0;
  const ownFuel = servedLevel(w, BuildingType.FuelDepot) >= 1;
  a.fuelFromDepot = ownFuel ? 1 : 0;
  a.contracted = ownHandling(w) ? 0 : 1;
  // The outside contractor is slower with baggage, cleaning and catering; a level 2 stand's own
  // equipment quicker with everything.
  const fast = fastStand(w, a.standId) ? FAST_STAND_TIME_PERCENT : 100;
  const handled = (m: number) =>
    Math.ceil(
      ((a.contracted ? (minutes(m) * CONTRACTOR_TIME_PERCENT) / 100 : minutes(m)) * fast) / 100,
    );
  // Freight: no passengers, no cabin; unloading and loading take as long as the tonnage and
  // the cargo terminal make them.
  const tonnes = f?.cargo ?? 0;
  const freight = (perTonne: number) => handled(Math.ceil((tonnes * perTonne) / 1000));
  const cargoLevel = Math.min(
    servedLevel(w, BuildingType.CargoTerminal),
    CARGO_MILLIMINUTES_PER_TONNE.length - 1,
  );
  const perTonne = CARGO_MILLIMINUTES_PER_TONNE[cargoLevel] ?? 2000;
  // Without its own depot the airport waits for an outside fuel supplier.
  const fuelling = Math.ceil((minutes(def.fuelMinutes * (ownFuel ? 1 : 2)) * fast) / 100);
  const plan =
    tonnes > 0
      ? [0, freight(perTonne), fuelling, 0, 0, freight(perTonne)]
      : [
          Math.ceil((paxIn * TICKS_PER_GAME_MINUTE) / doorRate(w, a.standId)),
          handled(def.unloadMinutes),
          fuelling,
          handled(def.cleanMinutes),
          // A flight kitchen's own trucks load faster.
          Math.ceil(
            (handled(def.cateringMinutes) *
              (servedLevel(w, BuildingType.CateringKitchen) ? CATERING_TIME_PERCENT : 100)) /
              100,
          ),
          handled(def.loadMinutes),
        ];
  a.svcLeft = plan.slice();
  a.svcTotal = plan.slice();
  a.boardState = 0;
  a.boardMilli = 0;
  a.gse = 0;
  // Now and then the walk-around finds a defect. A hangar means fewer defects and engineers on
  // site; without one they come from elsewhere.
  const hangar = Math.min(bestLevel(w, BuildingType.Hangar), DEFECT_PERMILLE.length - 1);
  const defect = w.rng.int(1000) < (DEFECT_PERMILLE[hangar] ?? 0);
  // A tyre cut by debris on landing (see touchdownWear) is changed here too.
  a.techTotal = (defect ? minutes(REPAIR_MINUTES[hangar] ?? 0) : 0) + a.techLeft;
  // A bird strike means checking and fixing the damage before the next flight.
  if (a.emergency === EmergencyKind.BirdStrike) a.techTotal += minutes(BIRD_STRIKE_REPAIR_MINUTES);
  a.techLeft = a.techTotal;
  a.deiceLeft = 0;
  a.deiceTotal = 0;
  a.gseEta = new Array<number>(GSE_COUNT).fill(0);
  // A medical emergency: passengers stay on board until first aid from the fire station arrives.
  if (a.emergency === EmergencyKind.Medical) {
    a.svcLeft[Service.Deboard] = (a.svcLeft[Service.Deboard] ?? 0) + firstAid(w, a.standId);
    a.svcTotal[Service.Deboard] = a.svcLeft[Service.Deboard] ?? 0;
    if (f) {
      w.emit({
        type: 'emergency',
        stage: 'landed',
        kind: EmergencyKind.Medical,
        flightId: f.id,
        airline: f.airline,
        runwayId: a.runwayId,
      });
    }
  }
  // The outside contractor drives in over the motorway before any service can start.
  const stand = w.buildings.find((b) => b.id === a.standId);
  a.crewEta = a.contracted && stand ? Math.max(0, w.airfield().serviceTicks(null, stand)) : 0;
}

/** Freezing weather when the aircraft is ready: plan de-icing (and pay the contractor). */
function prepareDeparture(w: OpsWorld, a: Aircraft, fleet: Fleet): void {
  if (!icing(w, Math.floor(minuteOfDay(w.time) / 60))) return;
  const def = AIRCRAFT_CLASSES[a.cls];
  const ticks = minutes(def.deiceMinutes);
  if (hasPad(w, a.cls)) {
    // The pad's fixed gantries spray faster than a truck, and cost nothing more per aircraft.
    a.deiceTotal = Math.ceil((ticks * PAD_TIME_PERCENT) / 100);
    a.deiceLeft = a.deiceTotal;
    return;
  }
  const own = (fleet.total[Gse.Deicer] ?? 0) > 0;
  a.deiceTotal = own ? ticks : Math.ceil((ticks * CONTRACTOR_TIME_PERCENT) / 100);
  a.deiceLeft = a.deiceTotal;
  if (!own) {
    w.money -= DEICE_FEE[a.cls];
    w.today.handling += DEICE_FEE[a.cls];
  }
}

/**
 * After an emergency landing the runway is closed for inspection and clean-up: shorter with a
 * better fire station (faster crews, better equipment).
 */
function emergencyLanded(w: OpsWorld, a: Aircraft): void {
  const level = Math.min(
    bestLevel(w, BuildingType.FireStation),
    EMERGENCY_CLOSURE_MINUTES.length - 1,
  );
  const until = w.time + minutes(EMERGENCY_CLOSURE_MINUTES[level] ?? 30);
  w.runwayClosures.push({
    runwayId: a.runwayId,
    from: w.time,
    until,
    kind: ClosureKind.Emergency,
  });
  const f = w.flights.find((x) => x.id === a.flightId);
  w.emit({
    type: 'emergency',
    stage: 'landed',
    kind: a.emergency as EmergencyKindId,
    flightId: a.flightId,
    airline: f?.airline ?? a.airline,
    runwayId: a.runwayId,
  });
  // A safe landing handled well is good news.
  stir(w, 60);
}

/** Ticks until first aid from the nearest fire station reaches a stand by road. */
function firstAid(w: OpsWorld, standId: number): number {
  const stand = w.buildings.find((b) => b.id === standId);
  const airfield = w.airfield();
  let best = -1;
  if (stand) {
    for (const b of w.buildings) {
      if (b.type !== BuildingType.FireStation || b.level < 1) continue;
      const d = airfield.serviceTicks(b, stand);
      if (d >= 0 && (best < 0 || d < best)) best = d;
    }
  }
  return best < 0 ? minutes(MEDICAL_FALLBACK_MINUTES) : best + minutes(5);
}

// ---------------------------------------------------------------------------------------------
// Daily goals (see daily.ts)

/** A day's figures as the goals count them (rewards are not income here). */
function goalProgress(l: Ledger): GoalProgress {
  return {
    departures: l.departures,
    passengers: l.passengers,
    income: ledgerIncome(l) - l.rewards,
    tonnes: l.tonnes,
    onTime: l.onTime,
    cancelled: l.cancelled,
  };
}

function payGoal(w: OpsWorld, goal: DailyGoal): void {
  goal.state = GoalState.Met;
  w.money += goal.reward;
  w.today.rewards += goal.reward;
  w.emit({ type: 'dailyGoal', stage: 'met', kind: goal.kind, reward: goal.reward });
}

/** Today's goals, sized on yesterday. */
function newGoals(w: OpsWorld): void {
  // Drawn from a generator of their own (the world's seed and the day), so they leave the
  // world's random numbers, and everything drawn from them, as they were.
  const draw = new Rng((w.seed ^ Math.imul(dayIndex(w.time) + 1, 0x9e3779b1)) >>> 0);
  w.dailyGoals.splice(0, w.dailyGoals.length, ...rollGoals(draw, goalProgress(w.yesterday)));
  w.emit({ type: 'dailyGoal', stage: 'new', kind: 0, reward: 0 });
}

/** Goals met by today's figures so far (those judged at the end of the day wait). */
function checkGoals(w: OpsWorld): void {
  const today = goalProgress(w.today);
  for (const goal of w.dailyGoals) {
    if (goal.state === GoalState.Open && !judgedAtDayEnd(goal) && goalMet(goal, today)) {
      payGoal(w, goal);
    }
  }
}

/** The day is over (its ledger is now yesterday's): the day-long goals are judged. */
function endGoals(w: OpsWorld): void {
  const day = goalProgress(w.yesterday);
  for (const goal of w.dailyGoals) {
    if (goal.state !== GoalState.Open) continue;
    if (judgedAtDayEnd(goal) && goalMet(goal, day)) payGoal(w, goal);
    else {
      goal.state = GoalState.Missed;
      w.emit({ type: 'dailyGoal', stage: 'missed', kind: goal.kind, reward: goal.reward });
    }
  }
}

/** Pays out every milestone reached since the last check (each one once). */
function checkMilestones(w: OpsWorld): void {
  if (MILESTONES.every((m) => milestoneDone(w.milestones, m.id))) return;
  const airfield = w.airfield();
  const facts: MilestoneFacts = {
    best: (type) => bestLevel(w, type),
    roadAccess: w.buildings.some(
      (b) => b.type === BuildingType.Terminal && b.level >= 1 && airfield.landsideConnected(b),
    ),
    standRoad: w.buildings.some(
      (b) =>
        (CIVIL_STANDS.has(b.type) || b.type === BuildingType.ContactStand) &&
        b.level >= 1 &&
        airfield.serviceConnected(b),
    ),
    connected: airfield.stands.some((stand) => {
      if (stand.kind === StandKind.Pad) return false;
      const dist = airfield.distancesTo(stand);
      return airfield.runways.some((rw) => rw.access.some((n) => n >= 0 && (dist[n] ?? -1) >= 0));
    }),
    contracts: w.contracts.length,
    departures: w.departuresByClass.reduce((n, d) => n + d, 0),
    rating: w.rating,
    money: w.money,
    departuresOf: (cls) => w.departuresByClass[cls] ?? 0,
    freight: w.today.tonnes + w.yesterday.tonnes,
    transfers: Math.max(w.today.transfers, w.yesterday.transfers),
    international: w.contracts.filter((c) => isInternational(c.airline)).length,
    charters: w.contracts.filter((c) => c.event > 0).length,
    value: airportValue(w),
  };
  for (const m of MILESTONES) {
    if (milestoneDone(w.milestones, m.id) || !m.done(facts)) continue;
    w.milestones = withMilestone(w.milestones, m.id);
    w.money += m.reward;
    w.today.rewards += m.reward;
    w.emit({ type: 'milestone', id: m.id, reward: m.reward });
  }
}

/**
 * Reopens runways whose inspection, works or ploughing are over (resurfaced ones as new, ploughed
 * ones clear of snow); sends the crews to a runway snowed in for too long; sees the plough teams
 * home.
 */
function reopenRunways(w: OpsWorld): void {
  for (let i = w.snowJobs.length - 1; i >= 0; i--) {
    if ((w.snowJobs[i] as SnowJob).home <= w.time) w.snowJobs.splice(i, 1);
  }
  for (let i = w.runwayClosures.length - 1; i >= 0; i--) {
    const c = w.runwayClosures[i] as RunwayClosure;
    if (c.kind === ClosureKind.Snow) {
      if (c.until > w.time) continue;
      const job = w.snowJobs.find((j) => j.runwayId === c.runwayId);
      const runway = w.buildings.find((b) => b.id === c.runwayId);
      if (!job && runway) {
        // Nobody sent the ploughs: the crews go out by themselves at last.
        const sent = ploughJob(w.buildings, runway, w.time, w.ground.highwayRow());
        w.snowJobs.push(sent);
        w.runwayClosures[i] = { ...c, until: sent.done };
        if (sent.baseId === 0) {
          w.money -= PLOUGH_CONTRACTOR_FEE;
          w.today.handling += PLOUGH_CONTRACTOR_FEE;
        }
        w.emit({ type: 'snow', stage: 'overdue', runwayId: c.runwayId });
        continue;
      }
      w.runwayClosures.splice(i, 1);
      setSnowDepth(w.runwaySnow, c.runwayId, 0);
      w.emit({ type: 'snow', stage: 'cleared', runwayId: c.runwayId });
      continue;
    }
    if (c.kind === ClosureKind.Works) {
      if (c.from <= w.time && c.from > w.time - TICKS_PER_GAME_MINUTE) {
        w.emit({ type: 'runwayWorks', stage: 'started', runwayId: c.runwayId });
      }
      if (c.until > w.time) continue;
      w.runwayClosures.splice(i, 1);
      const b = w.buildings.find((x) => x.id === c.runwayId);
      if (b) b.wear = 0;
      w.buildingsDirty = true;
      w.emit({ type: 'runwayWorks', stage: 'done', runwayId: c.runwayId });
      continue;
    }
    if (c.until > w.time) continue;
    w.runwayClosures.splice(i, 1);
    if (c.kind === ClosureKind.Scramble) continue;
    if (c.kind === ClosureKind.Flyover) {
      // The last of them open again: the flypast is over.
      if (!w.runwayClosures.some((x) => x.kind === ClosureKind.Flyover)) {
        w.emit({ type: 'flyover', stage: 'over', runwayIds: [], minutes: 0, compensation: 0 });
      }
      continue;
    }
    w.emit({
      type: 'emergency',
      stage: 'cleared',
      kind: EmergencyKind.None,
      flightId: 0,
      airline: 0,
      runwayId: c.runwayId,
    });
  }
}

/** Vehicles (and the contractor's crew) on their way to the aircraft come one tick closer. */
function driveOver(a: Aircraft): void {
  if (a.crewEta > 0) a.crewEta--;
  for (let k = 0; k < GSE_COUNT; k++) {
    if ((a.gseEta[k] ?? 0) > 0) a.gseEta[k] = (a.gseEta[k] as number) - 1;
  }
}

/** Whether a service may run now (its predecessors are done). */
export function serviceUnblocked(svcLeft: readonly number[], service: number): boolean {
  switch (service) {
    case Service.Clean:
    case Service.Catering:
      return svcLeft[Service.Deboard] === 0;
    case Service.Load:
      return svcLeft[Service.Unload] === 0;
    default:
      return true;
  }
}

/**
 * Vehicles a service needs (Gse bit mask): stairs and a bus at remote stands, a baggage train,
 * a catering truck, a cleaning van and, with the airport's own depot, a fuel truck.
 */
export function serviceVehicles(service: number, remote: boolean, ownFuel: boolean): number {
  switch (service) {
    case Service.Deboard:
      return remote ? REMOTE_DOOR_VEHICLES : 0;
    case Service.Unload:
    case Service.Load:
      return gseBit(Gse.Baggage);
    case Service.Fuel:
      return ownFuel ? gseBit(Gse.Fuel) : 0;
    case Service.Clean:
      return gseBit(Gse.Cleaning);
    case Service.Catering:
      return gseBit(Gse.Catering);
    default:
      return 0;
  }
}

function turnaround(w: OpsWorld, a: Aircraft, fleet: Fleet): void {
  // A thunderstorm overhead: lightning alert, everyone off the apron until it passes.
  if (w.sky === Sky.Storm) {
    const f = w.flights.find((x) => x.id === a.flightId);
    if (f && w.time % TICKS_PER_GAME_MINUTE === 0) f.lostWeather++;
    return;
  }
  // A dust storm: the crews work on in goggles and masks, at half the pace (every other tick
  // stands still, and every other minute is lost to the weather).
  if (w.sky === Sky.Dust && w.time % 2 === 1) {
    const f = w.flights.find((x) => x.id === a.flightId);
    const minute = Math.floor(w.time / TICKS_PER_GAME_MINUTE);
    if (f && minute % 2 === 1 && w.time % TICKS_PER_GAME_MINUTE === 1) f.lostWeather++;
    return;
  }
  const remote = !bridged(w, a.standId);
  const ownFuel = a.fuelFromDepot === 1;
  const onMinute = w.time % TICKS_PER_GAME_MINUTE === 0;
  let waitingForVehicle = false;

  // Decide what may run from the state at the start of the tick, then advance it. A service
  // starts only once all its vehicles are free and have driven over to the aircraft; it keeps
  // them until it is done.
  // Staying the night or parked for days: only what follows the arrival now (passengers off,
  // bags out, the cabin cleaned); fuel, meals and loading once it is back for its departure.
  const flight = w.flights.find((x) => x.id === a.flightId);
  const early = flight !== undefined && beforeTowBack(w, flight);
  const runnable = a.svcLeft.map(
    (left, i) => left > 0 && serviceUnblocked(a.svcLeft, i) && (!early || ARRIVAL_SERVICES.has(i)),
  );
  const travel = (k: number) => vehicleTravel(w, a, k);
  driveOver(a);
  // Engineers work on a defect alongside the other services.
  if (a.techLeft > 0) a.techLeft--;
  for (let i = 0; i < SERVICE_COUNT; i++) {
    if (!runnable[i]) continue;
    const mask = ownVehicles(fleet, serviceVehicles(i, remote, ownFuel));
    if (!acquire(fleet, a, mask, travel)) {
      waitingForVehicle = true;
      continue;
    }
    // The contractor's crew, or our own vehicles, still on the way.
    if (a.crewEta > 0 || !arrived(a, mask)) {
      waitingForVehicle = true;
      continue;
    }
    a.svcLeft[i] = (a.svcLeft[i] as number) - 1;
  }

  const f = w.flights.find((x) => x.id === a.flightId);
  if (!f) {
    release(fleet, a, 0);
    a.phase = Phase.Ready;
    return;
  }
  const def = AIRCRAFT_CLASSES[a.cls];
  const cabinReady = a.svcLeft[Service.Clean] === 0 && a.svcLeft[Service.Catering] === 0;
  const boardingDue =
    a.boardState === 0 && cabinReady && w.time >= f.departAt - minutes(def.boardingLeadMinutes);
  if (boardingDue) {
    // Remote stands board by bus and stairs, once they have driven over (not freighters).
    const doors = ownVehicles(fleet, remote && f.cargo === 0 ? REMOTE_DOOR_VEHICLES : 0);
    if (acquire(fleet, a, doors, travel) && a.crewEta === 0 && arrived(a, doors)) {
      a.boardState = 1;
    } else {
      waitingForVehicle = true;
    }
  }
  if (a.boardState === 1) {
    // Boarding speed is set by the doors; nobody boards before clearing security.
    const perTick = Math.floor((doorRate(w, a.standId) * 1000) / TICKS_PER_GAME_MINUTE);
    a.boardMilli = Math.min(a.boardMilli + perTick, f.paxReady * 1000);
    f.paxBoarded = Math.min(Math.floor(a.boardMilli / 1000), f.paxOut);
    const everyone = f.paxBoarded >= f.paxOut;
    const giveUp =
      w.time >= f.departAt + minutes(BOARDING_GRACE_MINUTES) && f.paxBoarded >= f.paxReady;
    // The gate is held for passengers still in the terminal queue.
    if (onMinute && !everyone && w.time >= f.departAt && f.paxBoarded >= f.paxReady) {
      f.lostPassengers++;
    }
    if (everyone || giveUp) {
      a.boardState = 2;
      f.gateClosed = 1;
    }
  }
  if (onMinute && waitingForVehicle) f.lostVehicles++;
  // Said on the flight card: it is the vehicles it waits for (once they come, it is not).
  if (waitingForVehicle) f.holdReason = HoldReason.NoVehicles;
  else if (f.holdReason === HoldReason.NoVehicles) f.holdReason = HoldReason.None;
  const serviced =
    a.boardState === 2 && a.svcLeft[Service.Fuel] === 0 && a.svcLeft[Service.Load] === 0;
  if (serviced && a.techLeft > 0 && onMinute) f.lostTechnical++;
  if (serviced && a.techLeft === 0) {
    a.phase = Phase.Ready;
    prepareDeparture(w, a, fleet);
  }

  // Hand back what is no longer needed: vehicles of finished services; at remote stands the
  // stairs stay from deboarding until the doors close, the bus only while passengers move.
  // A vehicle stays for a service that can go on right away (the baggage train from unloading
  // to loading) instead of driving back to the base and out again.
  let keep = 0;
  for (let i = 0; i < SERVICE_COUNT; i++) {
    // A long stay: the departure's services wait for its morning, and so do their vehicles
    // (or a parked aircraft would keep the baggage trains for days).
    if (early && !ARRIVAL_SERVICES.has(i)) continue;
    const left = a.svcLeft[i] as number;
    if (left > 0 && (left < (a.svcTotal[i] as number) || serviceUnblocked(a.svcLeft, i))) {
      keep |= serviceVehicles(i, remote, ownFuel);
    }
  }
  // The bus and stairs stay once called for boarding, also while still driving over.
  if (remote && (a.boardState === 1 || boardingDue)) keep |= REMOTE_DOOR_VEHICLES;
  // A long stay with its arrival work done waits to be towed off: the tug called for it stays
  // (handed back here, it would never get to the aircraft and the tow never happen).
  const arrivalDone = [...ARRIVAL_SERVICES].every((i) => (a.svcLeft[i] ?? 0) === 0);
  if (early && arrivalDone) keep |= a.gse & gseBit(Gse.Tug);
  const deboardStarted =
    (a.svcLeft[Service.Deboard] as number) < (a.svcTotal[Service.Deboard] as number);
  // The stairs stay from deboarding to boarding only when boarding is close; an aircraft with
  // hours to wait (a night stop, a long turnaround) gives them back, or it would keep every
  // other remote stand from deboarding and boarding until it leaves.
  const boardingSoon =
    w.time >= f.departAt - minutes(def.boardingLeadMinutes + STAIRS_STAY_MINUTES);
  if (
    remote &&
    a.boardState === 0 &&
    deboardStarted &&
    ((a.svcLeft[Service.Deboard] as number) > 0 || boardingSoon)
  ) {
    keep |= gseBit(Gse.Stairs);
  }
  release(fleet, a, keep);
}

// ---------------------------------------------------------------------------------------------
// Terminal: check-in and security queues

/** Throughput of each passenger stage over all terminals, passengers per minute. */
export interface TerminalStages {
  checkIn: number;
  security: number;
  passport: number;
}

export function terminalStages(
  buildings: readonly Building[],
  connected: (b: Building) => boolean = () => true,
): TerminalStages {
  const out: TerminalStages = { checkIn: 0, security: 0, passport: 0 };
  for (const b of buildings) {
    if (b.type !== BuildingType.Terminal || !connected(b)) continue;
    const base = TERMINAL_PAX_PER_MINUTE[b.level] ?? 0;
    // Extra check-in desks and security lanes widen their own stage.
    const p = stagePercents(b.facilities);
    out.checkIn += Math.floor((base * p.checkIn) / 100);
    out.security += Math.floor((base * p.security) / 100);
    out.passport += Math.floor((base * p.passport) / 100);
  }
  return out;
}

/** Domestic throughput, passengers per minute: the slower of check-in and security. */
export function terminalCapacity(
  buildings: readonly Building[],
  connected: (b: Building) => boolean = () => true,
): number {
  const s = terminalStages(buildings, connected);
  return Math.min(s.checkIn, s.security);
}

/** How the roads in coped in the last game minute (derived each minute, for display). */
export interface LandsideFlow {
  /** Passengers stuck on the way in (wanting to reach the terminal, held up in traffic). */
  waiting: number;
  /** Cars wanted against cars the roads let through, percent (0 when nothing limits them). */
  load: number;
  /** Cars a minute the narrowest junction on the way in lets through (-1: no limit). */
  cars: number;
  /** A tile of that junction (-1 if none). */
  tile: number;
  /** Cars a minute passing on the motorway now (they take their share of its junctions). */
  motorway: number;
}

export function emptyLandside(): LandsideFlow {
  return { waiting: 0, load: 0, cars: -1, tile: -1, motorway: 0 };
}

/** Passengers to a car, in tenths: taxis and private cars. Each bus station level adds more. */
const PAX_PER_CAR_TENTHS = 18;
const TRANSIT_TENTHS_PER_LEVEL = 6;
/** Passengers who come by train (with a railway station), percent: they need no road. */
const RAIL_SHARE_PERCENT = 25;

/**
 * Once per game minute, before check-in: departing passengers on their way (showedUp) reach the
 * terminal by road. The narrowest junction on the way in from the motorway lets so many cars a
 * minute through; the rest wait in traffic, the earliest departures first in line, and those who
 * get through too late miss their flight.
 */
function roadArrivals(w: OpsWorld): void {
  const motorway = motorwayCars(minuteOfDay(w.time));
  const cap = w.airfield().landsideCapacity(w.buildings, motorway);
  const flights = w.flights
    .filter(inTerminal)
    .sort((a, b) => a.departAt - b.departAt || a.id - b.id);
  let wanted = 0;
  for (const f of flights) wanted += Math.max(0, showedUp(f, w.time) - f.paxArrived);
  let left = wanted;
  let load = 0;
  if (cap.cars >= 0 && wanted > 0) {
    // Buses carry more to a vehicle (a bus station on the road by a terminal, as its income
    // and satisfaction count it); with a railway station some come by train.
    const perCar = PAX_PER_CAR_TENTHS + TRANSIT_TENTHS_PER_LEVEL * transitLevel(w);
    const rail =
      (bestLevel(w, BuildingType.RailStation) >= 1 ? RAIL_SHARE_PERCENT : 0) +
      (terminalFacility(w, Facility.Metro) > 0 ? METRO_SHARE_PERCENT : 0);
    const byRoad = Math.floor((cap.cars * perCar) / 10);
    left = Math.floor((byRoad * 100) / (100 - rail));
    const cars = Math.ceil((wanted * (100 - rail) * 10) / (100 * perCar));
    load = Math.floor((cars * 100) / Math.max(1, cap.cars));
  }
  let through = 0;
  for (const f of flights) {
    const want = Math.max(0, showedUp(f, w.time) - f.paxArrived);
    const take = Math.min(want, left);
    f.paxArrived += take;
    left -= take;
    through += take;
  }
  w.landside = {
    waiting: wanted - through,
    load: Math.min(999, load),
    cars: cap.cars,
    tile: cap.tile,
    motorway,
  };
}

/** Passengers of this flight who have set off for the airport by now (they come by road). */
export function showedUp(f: Flight, time: number): number {
  const span = SHOW_UP_START_MINUTES - SHOW_UP_END_MINUTES;
  const start = f.departAt - minutes(SHOW_UP_START_MINUTES);
  const since = Math.floor((time - start) / TICKS_PER_GAME_MINUTE);
  const elapsed = Math.max(0, Math.min(span, since));
  return Math.floor((f.paxOut * elapsed) / span);
}

/** Flights whose departing passengers are still on their way through the terminal (a
 * helicopter's come by car straight to its pad). */
export function inTerminal(f: Flight): boolean {
  return (
    f.paxOut > 0 && f.gateClosed === 0 && f.finishedAt === 0 && f.cls !== AircraftClass.Helicopter
  );
}

/**
 * Once per game minute: the passengers of every flight move through check-in, security and (on
 * international flights) passport control, each stage serving the earliest departures first up
 * to its throughput. The last stage goes first, so a passenger passes at most one stage a
 * minute. Everyone a stage cannot take this minute waits (and remembers it).
 */
function processTerminal(w: OpsWorld): void {
  // Passengers come by road: terminals without road access to the motorway get nobody.
  const airfield = w.airfield();
  const stages = terminalStages(w.buildings, (b) => airfield.landsideConnected(b));
  // Short-staffed desks, lanes and booths process fewer passengers.
  const staff = staffing(w, StaffRole.Terminal);
  let passport = Math.floor((stages.passport * staff) / 1000);
  let security = Math.floor((stages.security * staff) / 1000);
  let checkIn = Math.floor((stages.checkIn * staff) / 1000);
  const queue = w.flights.filter(inTerminal).sort((a, b) => a.departAt - b.departAt || a.id - b.id);
  for (const f of queue) {
    if (!isInternational(f.airline)) continue;
    const waiting = f.paxScreened - f.paxReady;
    const take = Math.min(waiting, passport);
    f.paxReady += take;
    passport -= take;
    f.waitSum += waiting - take;
  }
  for (const f of queue) {
    const waiting = f.paxCheckedIn - f.paxScreened;
    const take = Math.min(waiting, security);
    f.paxScreened += take;
    security -= take;
    f.waitSum += waiting - take;
    // Domestic passengers are airside once through security.
    if (!isInternational(f.airline)) f.paxReady = f.paxScreened;
  }
  for (const f of queue) {
    const waiting = f.paxArrived - f.paxCheckedIn;
    if (waiting <= 0) continue;
    const take = Math.min(waiting, checkIn);
    f.paxCheckedIn += take;
    checkIn -= take;
    f.waitSum += waiting - take;
  }
}

// ---------------------------------------------------------------------------------------------
// Departure: revenue and passenger satisfaction

function departed(w: OpsWorld, a: Aircraft): void {
  const f = w.flights.find((x) => x.id === a.flightId);
  if (!f) return;
  const def = AIRCRAFT_CLASSES[a.cls];
  // Freight and military sorties carry no passengers: no ratings, no talk of them.
  const unrated = f.cargo > 0 || def.military;
  const boarded = f.paxBoarded;
  const missed = f.paxOut - boarded;
  // Connecting passengers fill the empty seats (they need no check-in).
  const transfers =
    unrated || a.cls === AircraftClass.Helicopter
      ? 0
      : boardTransfers(w, Math.max(0, def.seats - boarded));
  f.transferOut = transfers;
  const flown = boarded + transfers;

  const landing = Math.floor((def.landingFee * w.landingFeePct) / 100);
  // International passengers pay a higher charge and shop duty free; terminal shops sell more
  // and a lounge earns a little from everyone.
  const intl = isInternational(f.airline);
  const charter = f.event;
  const shops = terminalFacility(w, Facility.Shops);
  const lounge = terminalFacility(w, Facility.Lounge) > 0;
  const passenger = Math.floor(
    (flown *
      PASSENGER_FEE *
      w.paxFeePct *
      (intl ? INTERNATIONAL_FEE_PERCENT : 100) *
      (charter ? CHARTER_FEE_PERCENT : 100)) /
      1000000,
  );
  const retail =
    Math.floor(
      (flown *
        (RETAIL_PER_PAX[bestLevel(w, BuildingType.Terminal)] ?? 0) *
        (intl ? INTERNATIONAL_RETAIL_PERCENT : 100) *
        (100 + SHOP_PERCENT * shops)) /
        10000,
    ) +
    (lounge ? flown * LOUNGE_PER_PAX : 0) +
    spotterIncome(w, a);
  const depot = servedLevel(w, BuildingType.FuelDepot);
  const fuel = a.fuelFromDepot
    ? Math.floor(
        (((def.fuelMargin * (100 + 15 * Math.max(0, depot - 1))) / 100) * marketFuel(w)) / 100,
      )
    : 0;
  let parkingLevels = 0;
  const airfield = w.airfield();
  for (const b of w.buildings) {
    if (b.type === BuildingType.Parking && airfield.landsideConnected(b)) parkingLevels += b.level;
  }
  const parking = boarded * PARKING_PER_PAX_PER_LEVEL * Math.min(parkingLevels, PARKING_MAX_LEVELS);
  // Toll plazas on the way in: those who come by road pay at the booth.
  const tolls = Math.floor((boarded * TOLL_PER_PAX * airfield.tolledPercent(w.buildings)) / 100);
  const hangar = bestLevel(w, BuildingType.Hangar);
  const maintenance = hangar
    ? Math.floor((MAINTENANCE_FEE[a.cls] * (100 + 50 * (hangar - 1))) / 100)
    : 0;
  // Taxis and buses at a transit hub by the terminal: a concession on every passenger.
  const hub = transitLevel(w);
  const rail = transitLevel(w, BuildingType.RailStation);
  // A metro station under the terminal: the airport's share of the fares.
  const metro = terminalFacility(w, Facility.Metro) > 0;
  const transit =
    boarded *
    ((TRANSIT_PER_PAX[hub] ?? 0) + (RAIL_PER_PAX[rail] ?? 0) + (metro ? METRO_PER_PAX : 0));
  // Freight: a handling charge per tonne, better with a bigger cargo terminal.
  const cargoLevel = servedLevel(w, BuildingType.CargoTerminal);
  const cargo = Math.floor(
    (f.cargo * CARGO_FEE_PER_TONNE * (100 + 25 * Math.max(0, cargoLevel - 1))) / 100,
  );
  // The flight kitchen sells the airline a meal for every passenger on board.
  const catering = flown * (CATERING_PER_PAX[servedLevel(w, BuildingType.CateringKitchen)] ?? 0);
  // The ministry pays for every military sortie.
  const military = def.military ? withAirDefence(w, def.militaryFee) : 0;
  const revenue =
    landing +
    passenger +
    retail +
    fuel +
    parking +
    tolls +
    maintenance +
    transit +
    cargo +
    catering +
    military;
  const handling = a.contracted ? HANDLING_FEE[a.cls] : 0;
  // Baggage: a bag a passenger, a few go astray; more with short crews or the contractor.
  const bags = unrated ? 0 : lostBags(w, a, flown);
  const compensation = bags * BAG_COMPENSATION;

  // Satisfaction: queueing, delay and people left behind all hurt.
  const avgWait = Math.floor(f.waitSum / Math.max(1, f.paxReady));
  const offBlock = f.offBlockAt || w.time;
  const late = Math.max(0, Math.floor((offBlock - f.departAt) / TICKS_PER_GAME_MINUTE));
  // Only the airport's share counts against it: a quick turnaround makes up inbound delay.
  const delay = Math.max(0, late - f.lateBy);
  const missedPct = Math.floor((missed * 100) / Math.max(1, f.paxOut));
  // Passengers also notice what the airport charges on their ticket.
  const penalty =
    Math.min(35, avgWait * 2) +
    Math.min(35, Math.floor(delay / 2)) +
    Math.floor(missedPct / 2) +
    fareGrudge(w.paxFeePct);
  const satisfaction = Math.max(
    0,
    Math.min(
      100,
      100 -
        penalty +
        (hub > 0 ? TRANSIT_SATISFACTION : 0) +
        (rail > 0 ? RAIL_SATISFACTION : 0) +
        (metro ? METRO_SATISFACTION : 0),
    ),
  );
  f.satisfaction = unrated ? Math.max(0, 100 - Math.min(100, delay)) : satisfaction;
  // What passengers tell about it, category by category, and online (freight has none).
  const bridge = bridged(w, f.standId);
  const fast = fastStand(w, f.standId);
  if (!unrated)
    rate(w, [
      1000 - Math.min(1000, delay * 25),
      1000 - Math.min(1000, avgWait * 40),
      (bridge ? 850 : fast ? 600 : 550) +
        (metro ? METRO_COMFORT : 0) +
        (hub > 0 ? 100 : 0) +
        (rail > 0 ? RAIL_COMFORT : 0) +
        (hotelRooms(w) > 0 ? HOTEL_COMFORT : 0) +
        (cityComfort({
          buildings: w.buildings,
          connected: (b) => w.airfield().landsideConnected(b),
        })
          ? CITY_COMFORT
          : 0) +
        (lounge ? LOUNGE_COMFORT : 0),
      priceScore(w.paxFeePct),
      1000 - Math.min(1000, missedPct * 10 + bags * 30),
    ]);
  if (!unrated)
    talk(
      w,
      (satisfaction >= 85 ? 15 : 0) -
        (satisfaction <= 40 ? 40 : 0) -
        Math.min(80, missed) -
        Math.min(40, bags * 8) -
        (delay > 60 ? 30 : 0),
    );

  f.revenue = revenue;
  finishFlight(w, f, FlightStatus.Departed);
  w.money += revenue - handling;
  const t = w.today;
  t.handling += handling;
  t.lostArrival += f.lostArrival;
  t.lostVehicles += f.lostVehicles;
  t.lostPassengers += f.lostPassengers;
  t.lostDeparture += f.lostDeparture;
  t.lostTechnical += f.lostTechnical;
  t.lostInbound += f.lateBy;
  t.lostWeather += f.lostWeather;
  t.bagsLost += bags;
  t.penalties += compensation;
  w.money -= compensation;
  t.maintenance += maintenance;
  t.transit += transit;
  t.cargo += cargo;
  t.catering += catering;
  t.military += military;
  t.tonnes += f.cargo;
  t.landing += landing;
  t.passenger += passenger;
  t.retail += retail;
  t.fuel += fuel;
  t.parking += parking;
  t.tolls += tolls;
  t.departures++;
  w.departuresByClass[a.cls] = (w.departuresByClass[a.cls] ?? 0) + 1;
  celebrate(w);
  firstFlight(w, f.airline);
  t.passengers += flown + f.paxIn;
  t.transfers += transfers;
  t.missed += missed;
  // Punctuality statistics count every minute, whoever caused it.
  if (late <= ON_TIME_MINUTES) t.onTime++;
  t.delayMinutes += late;
  if (f.vip) vipLeaves(w, f, vipPleased(delay, missed));
  const c = w.contracts.find((x) => x.id === f.contractId);
  if (c) {
    c.served++;
    c.strikes = 0;
    c.revenue += revenue;
    // Airlines judge punctuality (beyond the on-time margin) and passengers left behind.
    // Airlines also weigh the fees they pay against the standard tariff.
    const grudge = feeGrudge(w.landingFeePct, w.paxFeePct);
    const airlineScore = Math.max(
      0,
      100 - Math.max(0, delay - ON_TIME_MINUTES) * 2 - Math.floor(missedPct / 2) - grudge,
    );
    c.score += Math.trunc((airlineScore - c.score) / 4);
    // The record behind it (the contract card).
    const r = c.report;
    if (delay <= ON_TIME_MINUTES) r.onTime++;
    else r.late++;
    r.delayMinutes += delay;
    r.lostVehicles += f.lostVehicles;
    r.lostPassengers += f.lostPassengers;
    r.lostDeparture += f.lostDeparture;
    r.lostArrival += f.lostArrival;
    r.lostTechnical += f.lostTechnical;
    r.booked += f.paxOut;
    r.missed += missed;
    r.bagsLost += bags;
    r.feePoints += grudge;
  }
  w.emit({
    type: 'flightDeparted',
    flightId: f.id,
    airline: f.airline,
    revenue,
    satisfaction: f.satisfaction,
    missed,
    delay,
    cargo: f.cargo > 0,
    bagsLost: bags,
  });
}

// ---------------------------------------------------------------------------------------------
// The per-tick system

export function updateOperations(w: OpsWorld): void {
  const time = w.time;
  const onMinute = time % TICKS_PER_GAME_MINUTE === 0;
  const minute = minuteOfDay(time);

  // Teams on automatic staffing follow the buildings as they open, grow and close.
  autoStaff(w);

  if (onMinute && minute === 0) {
    // New day: roll the ledger, then the bank takes its instalments.
    addLedger(w.allTime, w.today);
    w.yesterday = w.today;
    w.today = emptyLedger(dayIndex(time));
    w.today.loans += payInstalments(w);
    // Yesterday's day-long goals are judged, and today's are set.
    endGoals(w);
    newGoals(w);
  }
  if (onMinute) {
    if (w.dailyGoals.length === 0) newGoals(w);
    checkGoals(w);
  }
  if (onMinute && (minute === AIRSHOW_ANNOUNCE_MINUTE || minute === AIRSHOW_MINUTE)) {
    airshow(w, minute === AIRSHOW_MINUTE);
  }
  if (onMinute && minute >= FLYOVER_EARLIEST && minute <= FLYOVER_LATEST && minute % 5 === 0) {
    if (minute === flyoverMinute(w.seed, dayIndex(time))) flyover(w);
  }

  // The noise quota starts afresh every night.
  if (onMinute && minute === NIGHT_START) w.nightNoise = 0;

  if (time % TICKS_PER_GAME_HOUR === 0) {
    hourly(w);
  }

  if (onMinute) {
    reopenRunways(w);
    checkMilestones(w);
    if (updateRunwayFlows(w, w.buildings)) w.buildingsDirty = true;
    scheduleFlights(w, minute);
    roadArrivals(w);
    processTerminal(w);
  }

  const r = reservations(w);

  if (onMinute) {
    for (const f of w.flights) {
      if (f.status !== FlightStatus.Scheduled && f.status !== FlightStatus.Holding) continue;
      const eta = f.scheduledAt + minutes(f.lateBy);
      if (time < eta) continue;
      const reason = tryArrive(w, f, r);
      if (reason === HoldReason.None) continue;
      f.status = FlightStatus.Holding;
      f.holdReason = reason;
      f.lostArrival++;
      if (time - eta >= MAX_HOLD_MINUTES * TICKS_PER_GAME_MINUTE) cancel(w, f);
    }
  }

  // Ground vehicles in use, derived from what each aircraft holds (first come, first served).
  const fleet = groundFleet(servingBuildings(w), w.aircraft, staffing(w, StaffRole.Ground));

  if (time % 5 === 0) {
    // The tower clears departures in the order they are due (the most delayed first).
    const due = (a: Aircraft) => w.flights.find((f) => f.id === a.flightId);
    const ready = w.aircraft
      .filter((a) => a.phase === Phase.Ready)
      .map((a) => ({ a, f: due(a) }))
      .sort((x, y) => (x.f?.departAt ?? 0) - (y.f?.departAt ?? 0) || x.a.id - y.a.id);
    for (const { a, f } of ready) tryDepart(w, a, f, r, fleet);
  }

  for (let i = 0; i < w.aircraft.length; ) {
    const a = w.aircraft[i] as Aircraft;
    const on =
      a.cls === AircraftClass.Helicopter ? moveHelicopter(w, a) : moveAircraft(w, a, fleet);
    if (on) i++;
    else w.aircraft.splice(i, 1);
  }
}

function cancel(w: OpsWorld, f: Flight): void {
  const reason = f.holdReason;
  finishFlight(w, f, FlightStatus.Cancelled);
  w.money -= CANCELLATION_PENALTY;
  w.today.penalties += CANCELLATION_PENALTY;
  w.today.missed += f.paxOut;
  w.today.cancelled++;
  hit(w, RatingCategory.Reliability, -150);
  talk(w, -120);
  if (f.vip) vipLeaves(w, f, false);
  const c = w.contracts.find((x) => x.id === f.contractId);
  if (c) {
    c.cancelled++;
    c.score = Math.max(0, c.score - 20);
    // Flight after flight it cannot land: the airline suspends the contract.
    c.strikes++;
    if (c.strikes >= SUSPEND_STRIKES) suspend(w, c, 'cancellations');
  }
  w.emit({ type: 'flightCancelled', flightId: f.id, airline: f.airline, reason });
}

/** Moves the buzz and announces when the mood turns. */
function talk(w: OpsWorld, amount: number): void {
  const before = mood(w.buzz);
  stir(w, amount);
  const after = mood(w.buzz);
  if (after !== before) w.emit({ type: 'buzzChanged', mood: after });
}

function mood(buzz: number): 'crisis' | 'viral' | 'calm' {
  return buzz <= CRISIS_BUZZ ? 'crisis' : buzz >= VIRAL_BUZZ ? 'viral' : 'calm';
}

// ---------------------------------------------------------------------------------------------
// The airline album

/** An airline's first departure from here goes into the album. */
function firstFlight(w: OpsWorld, airline: number): void {
  const seen = w.airlinesSeen;
  if (seen.includes(airline)) return;
  let i = 0;
  while (i < seen.length && (seen[i] as number) < airline) i++;
  seen.splice(i, 0, airline);
  w.emit({ type: 'airlineFirst', airline, collected: seen.length });
}

// ---------------------------------------------------------------------------------------------
// Celebrations (see celebrations.ts)

/** A round number of departures reached: the owners send their congratulations. */
function celebrate(w: OpsWorld): void {
  let departures = 0;
  for (const n of w.departuresByClass) departures += n;
  const milestone = departureMilestone(departures);
  if (!milestone) return;
  w.money += milestone.reward;
  w.today.rewards += milestone.reward;
  talk(w, 100);
  w.emit({ type: 'celebration', departures, reward: milestone.reward });
}

// ---------------------------------------------------------------------------------------------
// Air shows (see airshow.ts)

/** Today's air show, if there is one and the airport is up to it: its news, or the show. */
function airshow(w: OpsWorld, flown: boolean): void {
  if (bestLevel(w, BuildingType.Tower) < AIRSHOW_TOWER_LEVEL) return;
  const airfield = w.airfield();
  let levels = 0;
  for (const b of w.buildings) {
    if (b.type !== BuildingType.SpotterDeck || b.level < 1 || b.buildRemaining > 0) continue;
    if (airfield.landsideConnected(b)) levels += b.level;
  }
  const runway = w.buildings.find(
    (b) => isRunway(b.type) && b.level >= 1 && b.buildRemaining === 0,
  );
  if (levels === 0 || !runway || !airshowDay(w.seed, dayIndex(w.time))) return;
  if (!flown) {
    w.emit({ type: 'airshow', stage: 'announced', runwayId: runway.id, income: 0 });
    return;
  }
  const income = levels * AIRSHOW_DECK_INCOME;
  w.money += income;
  w.today.retail += income;
  w.airshows++;
  talk(w, AIRSHOW_BUZZ);
  w.emit({ type: 'airshow', stage: 'flown', runwayId: runway.id, income });
}

// ---------------------------------------------------------------------------------------------
// Air Force flypasts (see flyover.ts)

/** The flypast comes over: every open runway closes for it, and the ministry pays. */
function flyover(w: OpsWorld): void {
  const runwayIds: number[] = [];
  for (const b of w.buildings) {
    if (!isRunway(b.type) || b.level < 1 || b.buildRemaining > 0) continue;
    if (w.runwayClosures.some((c) => c.runwayId === b.id && c.from <= w.time)) continue;
    w.runwayClosures.push({
      runwayId: b.id,
      from: w.time,
      until: w.time + minutes(FLYOVER_MINUTES),
      kind: ClosureKind.Flyover,
    });
    runwayIds.push(b.id);
  }
  if (runwayIds.length === 0) return;
  const compensation = runwayIds.length * FLYOVER_COMPENSATION;
  w.money += compensation;
  w.today.military += compensation;
  talk(w, FLYOVER_BUZZ);
  w.emit({ type: 'flyover', stage: 'notice', runwayIds, minutes: FLYOVER_MINUTES, compensation });
}

// ---------------------------------------------------------------------------------------------
// VIPs (see vip.ts)

/** Now and then a VIP books a passenger flight on the board (one at a time). Drawn on its own
 * so the rest of the world's luck is the same with or without them. */
function vipBooking(w: OpsWorld, hour: number): void {
  if (hour < VIP_FIRST_HOUR || hour > VIP_LAST_HOUR) return;
  if (bestLevel(w, BuildingType.Tower) < VIP_TOWER_LEVEL) return;
  if (w.flights.some((f) => f.vip && f.finishedAt === 0)) return;
  const draw = new Rng(
    (w.seed ^ Math.imul(Math.floor(w.time / TICKS_PER_GAME_HOUR) + 1, 0x85ebca6b)) >>> 0,
  );
  if (!rollVip(draw)) return;
  const notice = w.time + minutes(VIP_NOTICE_MINUTES);
  const candidates = w.flights.filter(
    (f) =>
      f.status === FlightStatus.Scheduled &&
      f.scheduledAt >= notice &&
      f.cargo === 0 &&
      f.paxOut > 0 &&
      f.park !== Stay.Rental &&
      vipKindsFor(f.cls).length > 0,
  );
  const f = candidates[draw.int(candidates.length)];
  if (!f) return;
  f.vip = pickVip(draw, vipKindsFor(f.cls));
  w.emit({
    type: 'vip',
    stage: 'announced',
    flightId: f.id,
    airline: f.airline,
    kind: f.vip,
    reward: 0,
  });
}

/** The VIP's flight has left (or will not): thanks and praise, or bad press. */
function vipLeaves(w: OpsWorld, f: Flight, pleased: boolean): void {
  const reward = pleased ? vipReward(f.vip) : 0;
  if (pleased) w.vipsPleased++;
  w.money += reward;
  w.today.rewards += reward;
  talk(w, pleased ? VIP_PRAISE : VIP_SCORN);
  w.emit({
    type: 'vip',
    stage: pleased ? 'pleased' : 'disappointed',
    flightId: f.id,
    airline: f.airline,
    kind: f.vip,
    reward,
  });
}

function hourly(w: OpsWorld): void {
  // An older airport's widebodies leave the medium stands once a large stand is ready.
  if (
    w.legacyWideStands &&
    w.buildings.some((b) => b.type === BuildingType.LargeStand && b.buildRemaining === 0)
  ) {
    w.legacyWideStands = false;
  }
  const hour = Math.floor(minuteOfDay(w.time) / 60);
  const moodBefore = mood(w.buzz);
  calmDown(w);
  if (mood(w.buzz) !== moodBefore) w.emit({ type: 'buzzChanged', mood: mood(w.buzz) });
  updateWind(w, w.rng);
  if (hour === 0) updateClimate(w, w.rng, typicalClimate(w.theme, season(w.time)));
  if (hour === 0) {
    for (const change of updateMarket(w, w.rng)) w.emit({ type: 'market', change });
  }
  updateWeatherAhead(w, w.rng, hour);
  vipBooking(w, hour);
  blizzard(w, hour);
  const grant = updateGrant(w, (amount) => {
    w.today.penalties += amount;
  });
  if (grant) w.emit(grant);

  // Upkeep: a share of everything built.
  let upkeep = 0;
  for (const b of w.buildings) {
    if (b.level >= 1) upkeep += Math.floor((b.spent * UPKEEP_PER_DAY_PERMILLE) / 1000 / 24);
  }
  w.money -= upkeep;
  w.today.upkeep += upkeep;

  // Aircraft on remote positions pay by the hour.
  for (const a of w.aircraft) {
    if (a.phase !== Phase.Parked) continue;
    const fee = AIRCRAFT_PARKING_PER_HOUR[a.cls];
    w.money += fee;
    w.today.aircraftParking += fee;
  }

  const wages = hourlyPayroll(w);
  w.money -= wages;
  w.today.wages += wages;

  // In the red, the bank charges interest by the hour.
  const interest = overdraftInterest(w.money);
  w.money -= interest;
  w.today.overdraft += interest;

  missConnections(w);
  winterService(w);
  labourRelations(w, hour);

  // The hotel's night: guests check in, the rooms are paid for.
  if (hour === 3) hotelNight(w);

  // The airport city: an hour of sales and rent.
  const city = cityHour({
    buildings: w.buildings,
    connected: (b) => w.airfield().landsideConnected(b),
    rating: w.rating,
    passengers: w.yesterday.passengers,
    hour,
  });
  w.money += city.sales + city.rent;
  w.today.commerce += city.sales;
  w.today.customers += city.customers;
  w.today.rent += city.rent;

  // Offers: drop expired ones, then maybe receive a new one. A well-rated airport hears from
  // airlines more often and may hold one more open offer.
  for (let i = w.offers.length - 1; i >= 0; i--) {
    if ((w.offers[i] as Offer).expiresAt <= w.time) w.offers.splice(i, 1);
  }
  const maxOffers = MAX_OPEN_OFFERS + (w.rating >= 750 ? 1 : 0);
  if (towerLevel(w.buildings) >= 1 && w.offers.length < maxOffers) {
    // Airlines compare tariffs: expensive airports hear from fewer of them.
    const base = 250 + Math.floor((w.rating * 3) / 4);
    const fees = offerRatePermille(w.landingFeePct, w.paxFeePct);
    const chance = Math.floor(
      (((((((base * fees) / 1000) * buzzOfferPermille(w.buzz)) / 1000) * seasonOffers(w.time)) /
        100) *
        marketOffers(w)) /
        100,
    );
    // Airlines think twice about an airport in the red.
    const promoted =
      w.time < w.promoUntil ? Math.floor((chance * ROADSHOW_OFFER_PERCENT) / 100) : chance;
    const wary = w.money < 0 ? Math.floor((promoted * OVERDRAFT_OFFER_PERCENT) / 100) : promoted;
    if (w.rng.int(1000) < wary) generateOffer(w);
  }
  // The ministry, separately from the airlines.
  const militaryChance = Math.floor(
    (MILITARY_OFFER_PERMILLE *
      (100 + MILITARY_TOWER_OFFER_PERCENT * bestLevel(w, BuildingType.MilitaryTower))) /
      100,
  );
  if (servedLevel(w, BuildingType.MilitaryGate) >= 1 && w.rng.int(1000) < militaryChance) {
    generateMilitaryOffer(w);
  }
  scramble(w);

  // Airlines that have lost patience with the airport walk away.
  for (let i = w.contracts.length - 1; i >= 0; i--) {
    const c = w.contracts[i] as Contract;
    if (c.served + c.cancelled >= 3 && c.score < SUSPEND_SCORE) suspend(w, c, 'score');
  }

  // Contracts that ran out. A satisfied airline offers to renew, a delighted one with an extra
  // daily flight.
  for (let i = w.contracts.length - 1; i >= 0; i--) {
    const c = w.contracts[i] as Contract;
    if (c.endsAt > w.time) continue;
    w.contracts.splice(i, 1);
    let renewalOfferId = 0;
    // A charter ends with its event; a satisfied regular airline offers to renew.
    if (c.score >= RENEWAL_MIN_SCORE && !c.event && !c.rental) {
      const bonus = c.score >= RENEWAL_BONUS_SCORE && c.cls !== 3 ? 1 : 0;
      const offer: Offer = {
        id: w.nextOpsId++,
        airline: c.airline,
        cls: c.cls,
        flightsPerDay: Math.min(MAX_FLIGHTS_PER_DAY, c.slots.length + bonus),
        slotOffset: 0,
        days: OFFER_DAYS,
        expiresAt: w.time + OFFER_LIFETIME_HOURS * TICKS_PER_GAME_HOUR,
        renewal: 1,
        // The same times again, plus a new one for a delighted airline.
        slots:
          bonus && c.slots.length < MAX_FLIGHTS_PER_DAY
            ? [...c.slots, ...planSlots(w, 1, litRunway(w), c.cargo === 1)].sort((a, b) => a - b)
            : [...c.slots],
        cargo: c.cargo,
        event: 0,
        rental: 0,
      };
      w.offers.push(offer);
      renewalOfferId = offer.id;
    }
    w.emit({ type: 'contractEnded', contractId: c.id, airline: c.airline, renewalOfferId });
  }

  // Forget flights that finished a while ago.
  for (let i = w.flights.length - 1; i >= 0; i--) {
    const f = w.flights[i] as Flight;
    if (f.finishedAt > 0 && w.time - f.finishedAt > FINISHED_RETENTION) w.flights.splice(i, 1);
  }
}

/**
 * Minutes a flight will arrive late from where it comes from, drawn when it appears on the
 * board: most are on time, some a little late, a few very late.
 */
function inboundDelay(w: OpsWorld): number {
  const roll = w.rng.int(100);
  if (roll < 62) return 0;
  if (roll < 82) return w.rng.range(5, 19);
  if (roll < 94) return w.rng.range(20, 44);
  if (roll < 99) return w.rng.range(45, 89);
  return w.rng.range(90, 150);
}

/** Flights appear on the board this long before their arrival slot. */
export const SCHEDULE_LEAD_MINUTES = 180;

/**
 * The shortest round trip (minutes) of an aircraft between leaving here and landing here again:
 * the flight out and back and the turnaround at the other end. The airline flies each of its
 * flights with the aircraft that, as planned, is back from its last trip here most recently in
 * time for it (so a tight schedule simply uses more aircraft); when that aircraft leaves late
 * and the delay eats the slack, the flight comes back late too.
 */
export const ROTATION_MINUTES: Readonly<Record<AircraftClassId, number>> = {
  1: 150,
  2: 180,
  3: 300,
  4: 120,
  5: 90,
  6: 180,
  7: 120,
  8: 30,
  9: 60,
  10: 420,
  11: 480,
};

/**
 * Reactionary delay (minutes): how late a flight due at `scheduledAt` arrives because the
 * contract's aircraft left here at `offBlock` (its earliest return is a round trip later).
 */
function knockOnMinutes(cls: AircraftClassId, offBlock: number, scheduledAt: number): number {
  const back = offBlock + minutes(ROTATION_MINUTES[cls]);
  return Math.max(0, Math.ceil((back - scheduledAt) / TICKS_PER_GAME_MINUTE));
}

/**
 * The flight whose aircraft flies the contract's arrival due at `at`: of the contract's flights
 * leaving here within the day before, the one planned to leave last that can still be back in
 * time as scheduled.
 */
function inboundAircraftFlight(
  w: OpsWorld,
  contractId: number,
  cls: AircraftClassId,
  at: number,
): Flight | undefined {
  const rotation = minutes(ROTATION_MINUTES[cls]);
  let best: Flight | undefined;
  for (const f of w.flights) {
    if (f.contractId !== contractId || f.status === FlightStatus.Cancelled) continue;
    if (f.departAt + rotation > at || f.departAt < at - 24 * TICKS_PER_GAME_HOUR) continue;
    if (!best || f.departAt > best.departAt) best = f;
  }
  return best;
}

/** At most until just before nightfall, on a runway without lights (the flight diverts then). */
function nightCap(w: OpsWorld, scheduledAt: number, late: number): number {
  const slotMinute = minuteOfDay(scheduledAt);
  const lit = litRunway(w);
  return lit || slotMinute >= NIGHT_START
    ? late
    : Math.min(late, Math.max(0, NIGHT_START - 1 - slotMinute));
}

/**
 * An aircraft pushed back late: the contract's next flight, already on the board, comes back
 * that much later unless the schedule leaves room for it.
 */
function propagateDelay(w: OpsWorld, f: Flight): void {
  for (const next of w.flights) {
    if (next.contractId !== f.contractId || next.status !== FlightStatus.Scheduled) continue;
    if (inboundAircraftFlight(w, next.contractId, next.cls, next.scheduledAt) !== f) continue;
    const late = nightCap(w, next.scheduledAt, knockOnMinutes(f.cls, w.time, next.scheduledAt));
    if (late > next.lateBy) next.lateBy = late;
  }
}

/** Creates each contract flight ahead of its arrival slot, with its passenger loads. */
function scheduleFlights(w: OpsWorld, minute: number): void {
  for (const c of w.contracts) {
    for (const slot of c.slots) {
      if ((slot - SCHEDULE_LEAD_MINUTES + 1440) % 1440 !== minute) continue;
      const scheduledAt = w.time + minutes(SCHEDULE_LEAD_MINUTES);
      if (scheduledAt >= c.endsAt) continue;
      // A rental brings its aircraft in once (empty, positioning) and parks it for its days.
      if (c.rental && w.flights.some((f) => f.contractId === c.id)) continue;
      const def = AIRCRAFT_CLASSES[c.cls];
      const cargo = c.cargo ? Math.floor((CARGO_TONNES[c.cls] * w.rng.range(70, 100)) / 100) : 0;
      // Load factors follow the weekday and the season.
      const shift = loadShift(scheduledAt) + marketLoad(w);
      const load = () =>
        c.event ? w.rng.range(92, 100) : Math.max(35, Math.min(100, w.rng.range(60, 96) + shift));
      // Connecting passengers hold their own seats: the share they take is not sold locally.
      const heli = c.cls === AircraftClass.Helicopter;
      const share = heli ? 0 : transferPercent(w);
      const empty = c.cargo || c.rental;
      const paxIn = empty ? 0 : Math.floor((def.seats * load()) / 100);
      const paxOut = empty ? 0 : Math.floor((def.seats * load() * (100 - share)) / 10000);
      // Late in the evening a passenger aircraft may stay the night and leave in the morning.
      const slotMinute = minuteOfDay(scheduledAt);
      const night =
        !empty &&
        !c.event &&
        !def.military &&
        !heli &&
        (slotMinute >= NIGHT_STOP_FROM || slotMinute < NIGHT_STOP_UNTIL) &&
        w.rng.int(100) < NIGHT_STOP_PERCENT;
      const turnaroundDepart = scheduledAt + minutes(SCHEDULE_TAXI_MINUTES + def.turnaroundMinutes);
      const morning =
        scheduledAt -
        minutes(slotMinute) +
        (slotMinute >= NIGHT_STOP_FROM ? TICKS_PER_GAME_DAY : 0) +
        minutes(NIGHT_STOP_DEPART_FROM + w.rng.int(NIGHT_STOP_DEPART_SPREAD));
      const park = c.rental ? Stay.Rental : night ? Stay.NightStop : Stay.Turnaround;
      const departAt =
        park === Stay.Rental
          ? scheduledAt + c.rental * TICKS_PER_GAME_DAY
          : park === Stay.NightStop
            ? Math.max(morning, turnaroundDepart)
            : turnaroundDepart;
      // The same aircraft flew the contract's previous flight: if it is still here, or left
      // late, it cannot be back on time (reactionary delay).
      const previous = inboundAircraftFlight(w, c.id, c.cls, scheduledAt);
      const knockOn = previous
        ? knockOnMinutes(
            c.cls,
            previous.offBlockAt || Math.max(w.time, previous.departAt),
            scheduledAt,
          )
        : 0;
      // Without runway lights a late inbound diverts rather than arrive after dark: its delay
      // stops short of the night (the airport is not blamed for what it cannot handle).
      const lateBy = nightCap(w, scheduledAt, Math.max(inboundDelay(w), knockOn));
      const transferIn = Math.floor((paxIn * share) / 100);
      w.flights.push({
        id: w.nextOpsId++,
        contractId: c.id,
        airline: c.airline,
        cls: c.cls,
        scheduledAt,
        status: FlightStatus.Scheduled,
        holdReason: HoldReason.None,
        aircraftId: 0,
        standId: 0,
        revenue: 0,
        finishedAt: 0,
        departAt,
        paxIn,
        paxOut,
        paxReady: 0,
        paxArrived: 0,
        paxCheckedIn: 0,
        paxScreened: 0,
        paxBoarded: 0,
        waitSum: 0,
        offBlockAt: 0,
        gateClosed: 0,
        satisfaction: 0,
        lostArrival: 0,
        lostVehicles: 0,
        lostPassengers: 0,
        lostDeparture: 0,
        lostTechnical: 0,
        cargo,
        lateBy,
        transferIn,
        transferOut: 0,
        event: c.event,
        lostWeather: 0,
        park,
        vip: 0,
      });
    }
  }
}
